<template>
  <main class="booking-v2" :lang="languageTag" data-booking-ui-version="v2" aria-labelledby="booking-v2-title">
    <section ref="pageShell" class="booking-v2__shell">
      <header class="booking-v2__header">
        <div class="booking-v2__ornament" aria-hidden="true">
          <span></span>
          <i></i>
          <span></span>
        </div>
        <p class="booking-v2__eyebrow">LAFIA PRINCESS RESERVATION</p>
        <h1 id="booking-v2-title">{{ copy.title }}</h1>
        <div class="booking-v2__progress" :aria-label="progressLabel">
          <span v-for="(step, index) in progressSteps" :key="step.key" :class="{ 'is-complete': step.complete, 'is-current': index === currentStepIndex }">
            <i aria-hidden="true"></i>
            <b>{{ step.label }}</b>
          </span>
        </div>
      </header>

      <div v-if="loading" class="booking-v2__loading" role="status">
        <v-progress-circular indeterminate color="#d64f79" size="42" />
        <span>{{ copy.loading }}</span>
      </div>

      <template v-else>
        <div class="booking-v2__concept-wrap">
          <div class="booking-v2__concept" :class="{ 'is-empty': !selectedConcept, 'is-locked': conceptLocked, 'is-feedback': recentlyUpdatedSection === 'concept' }">
            <div class="booking-v2__concept-copy">
              <div class="booking-v2__concept-heading">
                <small>{{ copy.conceptEyebrow }}</small>
                <span class="booking-v2__concept-title-row">
                  <strong>{{ selectedConcept ? conceptTitle(selectedConcept) : copy.chooseConcept }}</strong>
                  <v-icon v-if="conceptLocked" class="booking-v2__concept-lock" size="20">mdi-lock-outline</v-icon>
                </span>
                <em v-if="conceptLocked">{{ copy.fixed }}</em>
              </div>

              <button v-if="selectedConcept" type="button" class="booking-v2__detail-button" :aria-label="copy.conceptDetail" @click="openSheet('pattern', $event.currentTarget)">
                <span>{{ copy.conceptDetail }}</span>
                <v-icon size="18">mdi-chevron-right</v-icon>
              </button>
            </div>

            <button
              type="button"
              class="booking-v2__concept-image"
              :disabled="conceptLocked"
              :aria-label="selectedConcept ? `${copy.concept}: ${conceptTitle(selectedConcept)}` : copy.chooseConcept"
              @click="openSheet('concept', $event.currentTarget)"
            >
              <img v-if="selectedConcept && conceptImage(selectedConcept) && !hasImageError(`concept-${selectedConcept.id}`)" :src="conceptImage(selectedConcept)" :alt="conceptTitle(selectedConcept)" @error="markImageError(`concept-${selectedConcept.id}`)" />
              <div v-else class="booking-v2__concept-placeholder" aria-hidden="true">
                <v-icon size="34" color="#c99342">mdi-crown-outline</v-icon>
              </div>
            </button>
          </div>
        </div>

        <div class="booking-v2__summary" :aria-label="copy.summaryLabel">
          <button
            v-if="showStaff"
            type="button"
            class="booking-v2__summary-row"
            data-summary-key="staff"
            :class="{ 'is-feedback': recentlyUpdatedSection === 'staff' }"
            :disabled="!selectedConcept"
            :aria-label="`${copy.staff}: ${selectedStaff ? staffName(selectedStaff) : copy.chooseStaff}`"
            @click="openSheet('staff', $event.currentTarget)"
          >
            <span class="booking-v2__summary-icon"><v-icon size="21">mdi-account-star-outline</v-icon></span>
            <span class="booking-v2__summary-copy">
              <small>{{ copy.staff }}</small>
              <strong>{{ selectedStaff ? staffName(selectedStaff) : copy.chooseStaff }}</strong>
            </span>
            <v-icon class="booking-v2__summary-arrow" size="24">mdi-chevron-right</v-icon>
          </button>

          <button
            type="button"
            class="booking-v2__summary-row"
            data-summary-key="schedule"
            :class="{ 'is-feedback': recentlyUpdatedSection === 'schedule' }"
            :disabled="scheduleDisabled && !localSafetyPreview"
            :aria-label="`${copy.schedule}: ${scheduleSummary}`"
            @click="openSheet('schedule', $event.currentTarget)"
          >
            <span class="booking-v2__summary-icon"><v-icon size="21">mdi-calendar-heart</v-icon></span>
            <span class="booking-v2__summary-copy">
              <small>{{ copy.schedule }}</small>
              <strong>{{ scheduleSummary }}</strong>
            </span>
            <v-icon class="booking-v2__summary-arrow" size="24">mdi-chevron-right</v-icon>
          </button>

          <button
            type="button"
            class="booking-v2__summary-row"
            data-summary-key="product"
            :class="{ 'is-feedback': recentlyUpdatedSection === 'product' }"
            :disabled="!selectedTime && !localSafetyPreview"
            :aria-label="`${copy.product}: ${productSummary}`"
            @click="openSheet('product', $event.currentTarget)"
          >
            <span class="booking-v2__summary-icon"><v-icon size="21">mdi-diamond-stone</v-icon></span>
            <span class="booking-v2__summary-copy">
              <small>{{ copy.product }}</small>
              <strong>{{ productSummary }}</strong>
            </span>
            <v-icon class="booking-v2__summary-arrow" size="24">mdi-chevron-right</v-icon>
          </button>

          <button
            v-if="optionProducts.length"
            type="button"
            class="booking-v2__summary-row"
            data-summary-key="option"
            :class="{ 'is-feedback': recentlyUpdatedSection === 'option' }"
            :disabled="!selectedProduct"
            :aria-label="`${copy.option}: ${optionSummary}`"
            @click="openSheet('option', $event.currentTarget)"
          >
            <span class="booking-v2__summary-icon"><v-icon size="21">mdi-sparkles</v-icon></span>
            <span class="booking-v2__summary-copy">
              <small>{{ copy.option }}</small>
              <strong>{{ optionSummary }}</strong>
            </span>
            <v-icon class="booking-v2__summary-arrow" size="24">mdi-chevron-right</v-icon>
          </button>
        </div>
      </template>
    </section>

    <footer ref="checkout" class="booking-v2__checkout" :class="{ 'is-hidden': activeSheet || !checkoutVisible }">
      <div class="booking-v2__checkout-inner">
        <span class="booking-v2__total">
          <small>{{ copy.total }}</small>
          <transition name="booking-price" mode="out-in">
            <strong :key="`main-total-${totalPrice}`">{{ formatMoney(totalPrice) }}<em>{{ copy.currency }}</em></strong>
          </transition>
        </span>
        <button type="button" class="booking-v2__pay" :disabled="!paymentButtonEnabled || payLoading" @click="handlePayClick">
          <v-progress-circular v-if="payLoading" indeterminate color="#ffffff" size="22" width="2" />
          <span v-else>{{ copy.pay }}</span>
        </button>
      </div>
    </footer>

    <transition name="booking-sheet">
      <div v-if="activeSheet" class="booking-v2__overlay" role="presentation" @click.self="closeSheet">
        <section ref="sheet" class="booking-v2__sheet" role="dialog" aria-modal="true" :aria-labelledby="sheetTitleId" tabindex="-1">
          <div class="booking-v2__sheet-handle" aria-hidden="true"></div>
          <header class="booking-v2__sheet-header" :class="{ 'is-pattern': activeSheet === 'pattern', 'is-staff': activeSheet === 'staff', 'is-product-flow': isProductFlowSheet, 'has-back': showSheetBack }">
            <button v-if="showSheetBack" type="button" class="booking-v2__sheet-back" :aria-label="copy.back" @click="goBackInSheet">
              <v-icon size="26">mdi-chevron-left</v-icon>
            </button>
            <div class="booking-v2__sheet-heading">
              <template v-if="isProductFlowSheet">
                <transition :name="productFlowHeadingTransitionName" mode="out-in">
                  <div :key="productFlowStage" class="booking-v2__sheet-flow-title">
                    <h2 :id="sheetTitleId">{{ activeSheetTitle }}</h2>
                    <small v-if="activeSheetNumber">{{ activeSheetNumber }}</small>
                  </div>
                </transition>
              </template>
              <template v-else>
                <small v-if="activeSheetNumber">{{ activeSheetNumber }}</small>
                <h2 :id="sheetTitleId">{{ activeSheetTitle }}</h2>
              </template>
              <p v-if="activeSheet === 'pattern'">{{ copy.patternConsultation }}</p>
              <p v-else-if="activeSheet === 'staff'">
                <span>{{ copy.staffGuidance }}</span>
                <span>{{ copy.staffTimeGuidance }}</span>
              </p>
            </div>
            <button ref="closeButton" type="button" class="booking-v2__sheet-close" :aria-label="copy.close" @click="closeSheet">
              <v-icon size="24">mdi-close</v-icon>
            </button>
          </header>

          <div ref="sheetBody" class="booking-v2__sheet-body" :class="`booking-v2__sheet-body--${activeSheet}`">
            <template v-if="activeSheet === 'pattern'">
              <div v-if="patternImages.length" class="booking-v2__pattern-grid">
                <figure v-for="pattern in patternImages" :key="pattern.id" class="booking-v2__pattern-item">
                  <span class="booking-v2__pattern-badge">{{ pattern.label }}</span>
                  <img
                    v-if="!hasImageError(`pattern-${selectedConcept.id}-${pattern.id}`)"
                    :src="patternImage(pattern.image)"
                    :alt="`${copy.pattern} ${pattern.label}`"
                    @error="markImageError(`pattern-${selectedConcept.id}-${pattern.id}`)"
                  />
                  <span v-else class="booking-v2__pattern-placeholder" aria-hidden="true">
                    <v-icon size="30" color="#bd8738">mdi-image-outline</v-icon>
                  </span>
                </figure>
              </div>
              <p v-else class="booking-v2__empty">{{ copy.noPatterns }}</p>
            </template>

            <template v-else-if="activeSheet === 'concept'">
              <div v-if="displayConcepts.length" class="booking-v2__choice-list booking-v2__choice-list--visual">
                <button
                  v-for="(concept, index) in displayConcepts"
                  :key="concept.id"
                  type="button"
                  class="booking-v2__visual-choice booking-v2__list-entry"
                  :class="{ 'is-selected': selectedConcept && selectedConcept.id === concept.id }"
                  :style="{ '--entry-index': Math.min(index, 4) }"
                  @click="selectConcept(concept)"
                >
                  <span class="booking-v2__visual-media">
                    <img v-if="conceptImage(concept) && !hasImageError(`concept-list-${concept.id}`)" :src="conceptImage(concept)" :alt="conceptTitle(concept)" @error="markImageError(`concept-list-${concept.id}`)" />
                    <v-icon v-else size="28" color="#bd8738">mdi-crown-outline</v-icon>
                  </span>
                  <strong>{{ conceptTitle(concept) }}</strong>
                  <v-icon size="23">{{ selectedConcept && selectedConcept.id === concept.id ? "mdi-check-circle" : "mdi-chevron-right" }}</v-icon>
                </button>
              </div>
              <p v-else class="booking-v2__empty">{{ copy.noConcepts }}</p>
            </template>

            <template v-else-if="activeSheet === 'staff'">
              <transition name="booking-content-swap" mode="out-in">
                <div v-if="staffLoading" key="staff-loading" class="booking-v2__sheet-loading">
                  <v-progress-circular indeterminate color="#d64f79" size="36" />
                </div>
                <div v-else-if="staffList.length" key="staff-list" class="booking-v2__choice-list booking-v2__choice-list--visual">
                  <div
                    v-for="(staff, index) in staffList"
                    :key="staff.id"
                    class="booking-v2__staff-choice booking-v2__list-entry"
                    :class="{ 'is-selected': selectedStaffId === staff.id }"
                    :style="{ '--entry-index': Math.min(index, 4) }"
                  >
                    <button type="button" class="booking-v2__staff-select" :aria-label="`${staffName(staff)} ${copy.detail}`" @click="showStaffDetail(staff)">
                      <span class="booking-v2__staff-media">
                        <img v-if="staffImage(staff) && !hasImageError(`staff-${staff.id}`)" :src="staffImage(staff)" :alt="staffName(staff)" @error="markImageError(`staff-${staff.id}`)" />
                        <b v-else>{{ staffInitial(staff) }}</b>
                      </span>
                      <strong>{{ staffName(staff) }}</strong>
                      <v-icon size="23">{{ selectedStaffId === staff.id ? "mdi-check-circle" : "mdi-chevron-right" }}</v-icon>
                    </button>
                  </div>
                </div>
                <p v-else key="staff-empty" class="booking-v2__empty">{{ copy.noStaff }}</p>
              </transition>
            </template>

            <template v-else-if="activeSheet === 'schedule'">
              <div class="booking-v2__date-picker">
                <button type="button" class="booking-v2__date-step" :aria-label="copy.previousDay" @click="shiftBookingDate(-1)">
                  <v-icon size="24">mdi-chevron-left</v-icon>
                </button>
                <label class="booking-v2__date-current">
                  <span>{{ formatDate(date) }}</span>
                  <input type="date" :aria-label="copy.chooseDate" :value="nativeDateValue" :min="nativeDateMinimum" @change="handleNativeDateInput" />
                </label>
                <button type="button" class="booking-v2__date-step" :aria-label="copy.nextDay" @click="shiftBookingDate(1)">
                  <v-icon size="24">mdi-chevron-right</v-icon>
                </button>
              </div>
              <div class="booking-v2__schedule-divider"></div>
              <div class="booking-v2__time-area">
                <transition name="booking-content-swap" mode="out-in">
                  <div v-if="timeLoading" key="time-loading" class="booking-v2__sheet-loading">
                    <v-progress-circular indeterminate color="#d64f79" size="36" />
                  </div>
                  <p v-else-if="scheduleMessage" :key="`time-message-${scheduleMessage}`" class="booking-v2__empty">{{ scheduleMessage }}</p>
                  <div v-else key="time-list" class="booking-v2__time-grid">
                    <button
                      v-for="(time, index) in availableTimes"
                      :key="time"
                      type="button"
                      class="booking-v2__list-entry"
                      :style="{ '--entry-index': Math.min(index, 4) }"
                      :disabled="reservedTimes.includes(time)"
                      :class="{ 'is-selected': selectedTime === time }"
                      @click="selectTime(time)"
                    >
                      {{ time }}
                    </button>
                  </div>
                </transition>
              </div>
            </template>

            <template v-else-if="activeSheet === 'product' || activeSheet === 'option'">
              <transition :name="productFlowTransitionName" mode="out-in">
              <div v-if="productFlowStage === 'mode'" key="product-mode" class="booking-v2__product-flow">
                <div class="booking-v2__mode-grid">
                  <article class="booking-v2__mode-card" :class="{ 'is-selected': draftBookingMode === 'solo' }">
                    <button type="button" class="booking-v2__mode-select" :aria-pressed="draftBookingMode === 'solo'" @click="selectDraftBookingMode('solo')">
                      <span class="booking-v2__mode-image"><img :src="shootingModeThumbnails.solo" alt="" /></span>
                      <span class="booking-v2__mode-copy">
                        <strong>{{ copy.solo }}</strong>
                      </span>
                    </button>
                  </article>

                  <article class="booking-v2__mode-card" :class="{ 'is-selected': draftBookingMode === 'group' }">
                    <button type="button" class="booking-v2__mode-select" :aria-pressed="draftBookingMode === 'group'" @click="selectDraftBookingMode('group')">
                      <span class="booking-v2__mode-image"><img :src="shootingModeThumbnails.group" alt="" /></span>
                      <span class="booking-v2__mode-copy">
                        <strong>{{ copy.group }}</strong>
                      </span>
                    </button>
                    <transition name="booking-expand">
                      <div v-if="draftBookingMode === 'group'" class="booking-v2__mode-stepper">
                        <button type="button" :disabled="draftPersons <= 2" :aria-label="copy.decrease" @click="changeDraftPersons(-1)"><v-icon size="22">mdi-minus</v-icon></button>
                        <transition name="booking-count" mode="out-in">
                          <strong :key="`group-persons-${draftPersons}`">{{ draftPersons >= 2 ? draftPersons : copy.choosePeople }}</strong>
                        </transition>
                        <button type="button" :disabled="draftPersons >= groupMaximumPersons" :aria-label="copy.increase" @click="changeDraftPersons(1)"><v-icon size="22">mdi-plus</v-icon></button>
                      </div>
                    </transition>
                  </article>
                </div>
              </div>

              <div v-else-if="productFlowStage === 'tier'" key="product-tier" class="booking-v2__product-flow">
                <div v-if="tierChoices.length" class="booking-v2__benefit-list">
                  <button
                    v-for="choice in tierChoices"
                    :key="choice.tier"
                    type="button"
                    class="booking-v2__benefit-choice"
                    :class="{ 'is-selected': draftProductTier === choice.tier }"
                    :aria-pressed="draftProductTier === choice.tier"
                    @click="selectDraftProductTier(choice.tier)"
                  >
                    <span class="booking-v2__benefit-media" aria-hidden="true">
                      <span v-if="choice.tier === 'gold'" class="booking-v2__gold-print-stack">
                        <span class="booking-v2__print booking-v2__print--split">
                          <span v-for="(image, index) in goldPrintPreviewImages" :key="`gold-split-${index}`" class="booking-v2__print-split-panel">
                            <img :src="image" alt="" />
                          </span>
                        </span>
                        <span class="booking-v2__print booking-v2__print--single">
                          <img v-if="productPreviewImage" :src="productPreviewImage" alt="" />
                          <v-icon v-else size="27">mdi-image-outline</v-icon>
                        </span>
                      </span>
                      <v-icon v-else size="36">mdi-calendar-range</v-icon>
                    </span>
                    <span class="booking-v2__benefit-copy">
                      <small v-if="choice.tier === 'gold'" class="booking-v2__recommendation-badge">{{ copy.recommended }}</small>
                      <strong>{{ choice.tier === "gold" ? copy.todayPrint : copy.withinThreeDays }}</strong>
                      <span v-if="isGroupPriceMode" class="booking-v2__group-price-breakdown">{{ groupPriceBreakdown(choice.product) }}</span>
                      <em>{{ formatMoney(flowProductTotal(choice.product)) }}{{ copy.currency }}</em>
                    </span>
                    <v-icon size="25">{{ draftProductTier === choice.tier ? "mdi-radiobox-marked" : "mdi-radiobox-blank" }}</v-icon>
                  </button>
                </div>
                <p v-else class="booking-v2__empty">{{ copy.noProducts }}</p>
              </div>

              <div v-else-if="productFlowStage === 'pattern'" key="product-pattern" class="booking-v2__product-flow">
                <div class="booking-v2__pattern-choice-list">
                  <button
                    v-for="choice in availablePatternProducts"
                    :key="choice.product.id"
                    type="button"
                    class="booking-v2__pattern-choice"
                    :class="{ 'is-selected': draftPatternCount === choice.count }"
                    :aria-pressed="draftPatternCount === choice.count"
                    @click="selectDraftPatternCount(choice.count)"
                  >
                    <v-icon size="25">{{ draftPatternCount === choice.count ? "mdi-radiobox-marked" : "mdi-radiobox-blank" }}</v-icon>
                    <span class="booking-v2__pattern-choice-copy">
                      <span class="booking-v2__pattern-choice-heading">
                        <strong>{{ patternCountText(choice.count) }}</strong>
                        <small v-if="choice.count === 2" class="booking-v2__recommendation-badge">{{ copy.recommended }}</small>
                      </span>
                      <span v-if="isGroupPriceMode" class="booking-v2__group-price-breakdown">{{ groupPriceBreakdown(choice.product) }}</span>
                      <em>{{ formatMoney(flowProductTotal(choice.product)) }}{{ copy.currency }}</em>
                    </span>
                    <span class="booking-v2__print-stack" :class="`has-${choice.count}`" aria-hidden="true">
                      <span v-for="(image, index) in patternPreviewImages(choice.count)" :key="`${choice.product.id}-${index}`" class="booking-v2__print" :style="{ '--print-index': index }">
                        <img :src="image" alt="" />
                      </span>
                    </span>
                  </button>
                </div>
                <p class="booking-v2__flow-note">{{ copy.patternConsultation }}</p>
              </div>

              <div v-else key="product-addons" class="booking-v2__product-flow booking-v2__product-flow--addons">
                <div class="booking-v2__addon-intro">
                  <strong>{{ copy.addOnPrompt }}</strong>
                  <span>{{ copy.addOnHint }}</span>
                </div>
                <div v-if="visibleAddonOptions.length" class="booking-v2__addon-list">
                  <article v-for="option in visibleAddonOptions" :key="option.id" class="booking-v2__addon-choice" :class="{ 'is-selected': isOptionSelected(option.id), 'is-open': String(activeOptionDetailId) === String(option.id) }" :data-addon-id="option.id">
                    <div class="booking-v2__addon-row">
                      <button type="button" class="booking-v2__addon-toggle" :aria-label="optionTitle(option)" :aria-pressed="isOptionSelected(option.id)" @click="toggleOption(option)">
                        <v-icon size="26">{{ isOptionSelected(option.id) ? "mdi-checkbox-marked-circle" : "mdi-checkbox-blank-circle-outline" }}</v-icon>
                      </button>
                      <span class="booking-v2__addon-copy">
                        <strong>{{ optionTitle(option) }}</strong>
                        <em>+{{ formatMoney(flowOptionPrice(option)) }}{{ copy.currency }}</em>
                      </span>
                      <button type="button" class="booking-v2__addon-detail" :aria-expanded="String(activeOptionDetailId) === String(option.id)" @click="toggleOptionDetail(option)">
                        <span>{{ String(activeOptionDetailId) === String(option.id) ? copy.collapse : copy.detail }}</span>
                        <v-icon size="19" :class="{ 'is-open': String(activeOptionDetailId) === String(option.id) }">mdi-chevron-down</v-icon>
                      </button>
                    </div>
                    <transition
                      name="booking-addon-detail"
                      @before-enter="beforeAddonDetailEnter"
                      @enter="enterAddonDetail"
                      @after-enter="afterAddonDetailEnter"
                      @before-leave="beforeAddonDetailLeave"
                      @leave="leaveAddonDetail"
                      @after-leave="afterAddonDetailLeave"
                    >
                    <div v-if="String(activeOptionDetailId) === String(option.id)" class="booking-v2__addon-description" :class="{ 'is-logo-removal': isLogoRemovalOption(option), 'is-concept-addon': isConceptAddonOption(option), 'is-retouch-addon': isRetouchAddonOption(option) }">
                      <div v-if="isLogoRemovalOption(option)" class="booking-v2__logo-removal-detail">
                        <strong class="booking-v2__logo-removal-title">{{ copy.logoRemovalCleanTitle }}</strong>
                        <p class="booking-v2__logo-removal-note">{{ copy.logoRemovalComparisonDescription }}</p>
                        <div class="booking-v2__logo-removal-preview">
                          <figure class="booking-v2__logo-removal-shot">
                            <div class="booking-v2__logo-removal-photo">
                              <img :src="logoRemovalWithLogoImage" :alt="copy.logoRemovalWithLogo" />
                            </div>
                            <figcaption>{{ copy.logoRemovalWithLogo }}</figcaption>
                          </figure>
                          <v-icon class="booking-v2__logo-removal-arrow" size="20" aria-hidden="true">mdi-arrow-right</v-icon>
                          <figure class="booking-v2__logo-removal-shot">
                            <div class="booking-v2__logo-removal-photo">
                              <img :src="logoRemovalWithoutLogoImage" :alt="copy.logoRemovalWithoutLogo" />
                            </div>
                            <figcaption>{{ copy.logoRemovalWithoutLogo }}</figcaption>
                          </figure>
                        </div>
                        <strong class="booking-v2__logo-removal-use-title">{{ copy.logoRemovalUseTitle }}</strong>
                        <p class="booking-v2__logo-removal-use-copy">{{ copy.logoRemovalUseDescription }}</p>
                        <div class="booking-v2__logo-removal-uses">
                          <img class="booking-v2__logo-removal-use-cases" :src="logoRemovalUseCasesImage" alt="" />
                          <div class="booking-v2__logo-removal-use-labels">
                            <span v-for="use in logoRemovalUses" :key="use.key">{{ use.label }}</span>
                          </div>
                        </div>
                      </div>
                      <div v-else-if="isConceptAddonOption(option)" class="booking-v2__concept-addon-detail">
                        <strong class="booking-v2__concept-addon-title">{{ copy.conceptAddonTitle }}</strong>
                        <div v-if="productPreviewImage && addonPremiumPreview" class="booking-v2__concept-addon-comparison">
                          <figure class="booking-v2__concept-addon-shot">
                            <img :src="productPreviewImage" :alt="copy.selectedConceptLabel" />
                            <figcaption>{{ copy.selectedConceptLabel }}</figcaption>
                          </figure>
                          <span class="booking-v2__concept-addon-plus" aria-hidden="true"><v-icon size="22">mdi-plus</v-icon></span>
                          <figure class="booking-v2__concept-addon-shot">
                            <img
                              v-if="!hasImageError(`addon-premium-${addonPremiumPreview.key}`)"
                              :src="addonPremiumPreview.image"
                              :alt="copy.otherPremiumConceptLabel"
                              @error="markImageError(`addon-premium-${addonPremiumPreview.key}`)"
                            />
                            <figcaption>{{ copy.otherPremiumConceptLabel }}</figcaption>
                          </figure>
                        </div>
                        <p class="booking-v2__concept-addon-note">{{ copy.conceptAddonDescription }}</p>
                      </div>
                      <div v-else-if="isRetouchAddonOption(option)" class="booking-v2__retouch-addon-detail">
                        <strong class="booking-v2__retouch-addon-title">{{ copy.retouchAddonTitle }}</strong>
                        <div class="booking-v2__retouch-addon-comparison">
                          <figure class="booking-v2__retouch-addon-shot">
                            <img :src="retouchAddClosedEyesImage" :alt="copy.retouchAddonClosedPose" />
                          </figure>
                          <v-icon class="booking-v2__retouch-addon-plus" size="26" aria-hidden="true">mdi-plus</v-icon>
                          <figure class="booking-v2__retouch-addon-shot">
                            <img :src="retouchAddToothySmileImage" :alt="copy.retouchAddonSmilePose" />
                          </figure>
                        </div>
                        <p class="booking-v2__retouch-addon-note">{{ copy.retouchAddonDescription }}</p>
                      </div>
                      <p v-else>{{ optionSubtitle(option) }}</p>
                    </div>
                    </transition>
                    <div v-if="option.type === 'multiple' && isOptionSelected(option.id)" class="booking-v2__stepper">
                      <span>{{ copy.quantity }}</span>
                      <button type="button" :disabled="optionQuantity(option.id) <= 1" :aria-label="copy.decrease" @click="changeOptionQuantity(option, -1)"><v-icon size="21">mdi-minus</v-icon></button>
                      <transition name="booking-count" mode="out-in">
                        <strong :key="`${option.id}-${optionQuantity(option.id)}`">{{ optionQuantity(option.id) }}</strong>
                      </transition>
                      <button type="button" :disabled="optionQuantity(option.id) >= maxOptionQuantity(option)" :aria-label="copy.increase" @click="changeOptionQuantity(option, 1)"><v-icon size="21">mdi-plus</v-icon></button>
                    </div>
                  </article>
                </div>
                <p v-else class="booking-v2__empty">{{ copy.noOptions }}</p>
              </div>
              </transition>
            </template>
          </div>

          <footer v-if="isProductFlowSheet" class="booking-v2__sheet-footer booking-v2__sheet-footer--flow">
            <span v-if="hasProductFlowPrice" class="booking-v2__flow-total">
              <small>{{ copy.total }}</small>
              <transition name="booking-price" mode="out-in">
                <strong :key="`flow-total-${productFlowTotalPrice}`">{{ formatMoney(productFlowTotalPrice) }}<em>{{ copy.currency }}</em></strong>
              </transition>
            </span>
            <span class="booking-v2__flow-actions">
              <button type="button" :disabled="flowActionDisabled" @click="handleProductFlowAction">
                <transition name="booking-action-label" mode="out-in">
                  <span :key="productFlowStage">{{ flowActionLabel }}</span>
                </transition>
              </button>
            </span>
          </footer>
        </section>
      </div>
    </transition>
  </main>
</template>

<script>
import groupModeThumbnail from "@/views/booking/GPT/ui-v2/assets/product-mode-group-temporary.png"
import soloModeThumbnail from "@/views/booking/GPT/ui-v2/assets/product-mode-solo-temporary.png"
import logoRemovalWithLogoImage from "@/views/booking/GPT/ui-v2/assets/logo-removal-fixed/comparison-with-logo.png"
import logoRemovalWithoutLogoImage from "@/views/booking/GPT/ui-v2/assets/logo-removal-fixed/comparison-logo-free.png"
import logoRemovalUseCasesImage from "@/views/booking/GPT/ui-v2/assets/logo-removal-fixed/use-cases.png"
import retouchAddClosedEyesImage from "@/views/booking/GPT/ui-v2/assets/retouch-add-fixed/closed-eyes.png"
import retouchAddToothySmileImage from "@/views/booking/GPT/ui-v2/assets/retouch-add-fixed/toothy-smile.png"

const SHOOTING_MODE_THUMBNAILS = Object.freeze({
  solo: soloModeThumbnail,
  group: groupModeThumbnail,
})

const COPY = {
  ko: {
    title: "프린세스 예약",
    loading: "예약 정보를 불러오는 중",
    concept: "컨셉",
    conceptEyebrow: "CONCEPT",
    chooseConcept: "컨셉을 선택해 주세요",
    conceptDetail: "패턴 상세 보기",
    pattern: "패턴",
    patternConsultation: "정확히 촬영할 패턴은 당일 담당 메이커와 상담해 주세요.",
    noPatterns: "등록된 패턴 이미지가 없습니다.",
    fixed: "선택 고정",
    staff: "메이커",
    staffDialogTitle: "프린세스 메이커",
    staffGuidance: "담당 작가를 선택하세요.",
    staffTimeGuidance: "원하는 시간이 없으면 다른 메이커를 선택해 보세요.",
    chooseStaff: "메이커를 선택해 주세요",
    schedule: "날짜 · 시간",
    scheduleDialogTitle: "방문 날짜 선택",
    chooseSchedule: "날짜와 시간을 선택해 주세요",
    chooseDate: "방문 날짜 선택",
    previousDay: "이전 날짜",
    nextDay: "다음 날짜",
    product: "상품",
    chooseProduct: "상품을 선택해 주세요",
    option: "추가 옵션",
    chooseOption: "선택 안 함",
    summaryLabel: "예약 선택 내용",
    total: "총 결제금액",
    currency: "원",
    pay: "결제하기",
    close: "닫기",
    detail: "상세 보기",
    noConcepts: "선택할 수 있는 컨셉이 없습니다.",
    noStaff: "선택할 수 있는 메이커가 없습니다.",
    noTimes: "선택할 수 있는 시간이 없습니다.",
    noProducts: "선택할 수 있는 상품이 없습니다.",
    noOptions: "선택할 수 있는 옵션이 없습니다.",
    staffHoliday: "메이커 휴무일입니다.",
    pastDate: "지난 날짜는 선택할 수 없습니다.",
    sameDay: "당일 예약은 할 수 없습니다.",
    outsidePeriod: "예약 가능 기간이 아닙니다.",
    selectStaffFirst: "메이커를 먼저 선택해 주세요.",
    perPerson: "1인",
    people: "인원",
    peopleUnit: "명",
    quantity: "수량",
    decrease: "줄이기",
    increase: "늘리기",
    done: "선택 완료",
    progress: "예약 진행",
    back: "이전",
    solo: "솔로 샷",
    group: "그룹 샷",
    choosePeople: "인원 선택",
    productSelectTitle: "상품 선택",
    recommended: "라피아 추천",
    todayPrint: "오늘 완성 + 인화본",
    withinThreeDays: "3일 이내 완성",
    groupProducts: "그룹 촬영 상품 보기",
    groupChoiceQuestion: "그룹 상품을 선택해 주세요",
    patternCountTitle: "패턴 수 선택",
    addOnTitle: "추가 선택",
    addOnPrompt: "추가로 필요한 것이 있나요?",
    addOnHint: "다른 분들이 주로 선택하시는 옵션입니다.",
    collapse: "접기",
    currentSelection: "현재 선택",
    next: "다음",
    selectThisProduct: "이 상품 선택",
    completeSelection: "선택 완료",
    logoRemovalCleanTitle: "로고 없는 고해상도 원본",
    logoRemovalWithLogo: "로고 있음",
    logoRemovalWithoutLogo: "로고 없음",
    logoRemovalComparisonDescription: "사진 속 라피아 로고를 제거한 원본을 제공합니다.",
    logoRemovalUseTitle: "광고 · 쇼핑몰 · SNS에 바로 사용",
    logoRemovalUseDescription: "로고 삭제 파일과 상업 이용 허가를 함께 제공합니다.",
    logoRemovalSns: "SNS",
    logoRemovalOnline: "온라인몰",
    logoRemovalPromo: "홍보물",
    conceptAddonTitle: "한 번의 방문, 두 가지 공주 컨셉",
    selectedConceptLabel: "선택한 컨셉",
    otherPremiumConceptLabel: "다른 프리미엄 컨셉",
    conceptAddonDescription: "선택한 상품 혜택 그대로 한 컨셉을 더 촬영해요",
    retouchAddonTitle: "보정 사진 한 장 더",
    retouchAddonClosedPose: "눈을 감은 우아한 포즈",
    retouchAddonSmilePose: "카메라를 보며 밝게 웃는 포즈",
    retouchAddonDescription: "같은 컨셉, 다른 포즈의 보정본을 추가합니다.",
  },
  en: {
    title: "Princess Reservation",
    loading: "Loading reservation",
    concept: "Concept",
    conceptEyebrow: "CONCEPT",
    chooseConcept: "Choose a concept",
    conceptDetail: "View pattern details",
    pattern: "Pattern",
    patternConsultation: "Choose the shooting pattern after consulting with your photographer and maker on the day.",
    noPatterns: "No pattern images are available.",
    fixed: "Fixed",
    staff: "Maker",
    staffDialogTitle: "Princess Maker",
    staffGuidance: "Choose your photographer.",
    staffTimeGuidance: "If your preferred time is unavailable, try another maker.",
    chooseStaff: "Choose a maker",
    schedule: "Date · Time",
    scheduleDialogTitle: "Choose Visit Date",
    chooseSchedule: "Choose a date and time",
    chooseDate: "Choose visit date",
    previousDay: "Previous day",
    nextDay: "Next day",
    product: "Product",
    chooseProduct: "Choose a product",
    option: "Add-ons",
    chooseOption: "None selected",
    summaryLabel: "Booking selections",
    total: "Total",
    currency: " KRW",
    pay: "Pay",
    close: "Close",
    detail: "Details",
    noConcepts: "No concepts are available.",
    noStaff: "No makers are available.",
    noTimes: "No times are available.",
    noProducts: "No products are available.",
    noOptions: "No add-ons are available.",
    staffHoliday: "The maker is off today.",
    pastDate: "Past dates are unavailable.",
    sameDay: "Same-day booking is unavailable.",
    outsidePeriod: "This date is outside the booking period.",
    selectStaffFirst: "Choose a maker first.",
    perPerson: "per person",
    people: "Guests",
    peopleUnit: "people",
    quantity: "Qty",
    decrease: "Decrease",
    increase: "Increase",
    done: "Done",
    progress: "Booking progress",
    back: "Back",
    solo: "Solo Shot",
    group: "Group Shot",
    choosePeople: "Choose guests",
    productSelectTitle: "Choose Product",
    recommended: "LAFIA PICK",
    todayPrint: "Ready today + prints",
    withinThreeDays: "Ready within 3 days",
    groupProducts: "View group products",
    groupChoiceQuestion: "Choose a group product",
    patternCountTitle: "Choose Patterns",
    addOnTitle: "Add-ons",
    addOnPrompt: "Need anything extra?",
    addOnHint: "These are popular add-ons chosen by other guests.",
    collapse: "Close",
    currentSelection: "Current selection",
    next: "Next",
    selectThisProduct: "Choose this product",
    completeSelection: "Complete selection",
    logoRemovalCleanTitle: "Logo-free high-resolution original",
    logoRemovalWithLogo: "With logo",
    logoRemovalWithoutLogo: "Logo-free",
    logoRemovalComparisonDescription: "We provide the original image with the LAFIA logo removed.",
    logoRemovalUseTitle: "Ready for ads · shops · social",
    logoRemovalUseDescription: "Includes the logo-free file and commercial-use permission.",
    logoRemovalSns: "Social",
    logoRemovalOnline: "Online shop",
    logoRemovalPromo: "Promotional",
    conceptAddonTitle: "Two princess concepts in one visit",
    selectedConceptLabel: "Selected concept",
    otherPremiumConceptLabel: "Another premium concept",
    conceptAddonDescription: "Add one more concept with the same product benefits",
    retouchAddonTitle: "One more retouched photo",
    retouchAddonClosedPose: "Elegant pose with eyes closed",
    retouchAddonSmilePose: "Bright camera-facing smile",
    retouchAddonDescription: "Add another retouched photo from the same concept in a different pose.",
  },
  jp: {
    title: "プリンセス予約",
    loading: "予約情報を読み込み中",
    concept: "コンセプト",
    conceptEyebrow: "CONCEPT",
    chooseConcept: "コンセプトを選択してください",
    conceptDetail: "パターン詳細を見る",
    pattern: "パターン",
    patternConsultation: "撮影パターンは当日、担当フォトグラファーとメーカーに相談して決定します。",
    noPatterns: "登録されたパターン画像がありません。",
    fixed: "選択固定",
    staff: "メーカー",
    staffDialogTitle: "プリンセスメーカー",
    staffGuidance: "担当フォトグラファーを選択してください。",
    staffTimeGuidance: "希望する時間がない場合は、別のメーカーを選択してください。",
    chooseStaff: "メーカーを選択してください",
    schedule: "日付 · 時間",
    scheduleDialogTitle: "来店日を選択",
    chooseSchedule: "日付と時間を選択してください",
    chooseDate: "来店日を選択",
    previousDay: "前の日",
    nextDay: "次の日",
    product: "商品",
    chooseProduct: "商品を選択してください",
    option: "追加オプション",
    chooseOption: "選択なし",
    summaryLabel: "予約内容",
    total: "合計",
    currency: "ウォン",
    pay: "決済する",
    close: "閉じる",
    detail: "詳細",
    noConcepts: "選択できるコンセプトがありません。",
    noStaff: "選択できるメーカーがありません。",
    noTimes: "選択できる時間がありません。",
    noProducts: "選択できる商品がありません。",
    noOptions: "選択できるオプションがありません。",
    staffHoliday: "メーカーの休業日です。",
    pastDate: "過去の日付は選択できません。",
    sameDay: "当日予約はできません。",
    outsidePeriod: "予約可能期間外です。",
    selectStaffFirst: "先にメーカーを選択してください。",
    perPerson: "1名",
    people: "人数",
    peopleUnit: "名",
    quantity: "数量",
    decrease: "減らす",
    increase: "増やす",
    done: "選択完了",
    progress: "予約進行",
    back: "戻る",
    solo: "ソロショット",
    group: "グループショット",
    choosePeople: "人数を選択",
    productSelectTitle: "商品選択",
    recommended: "LAFIA おすすめ",
    todayPrint: "当日完成 + プリント付き",
    withinThreeDays: "3日以内に完成",
    groupProducts: "グループ撮影商品を見る",
    groupChoiceQuestion: "グループ商品を選択してください",
    patternCountTitle: "パターン数を選択",
    addOnTitle: "追加オプション",
    addOnPrompt: "追加で必要なものはありますか？",
    addOnHint: "ほかのお客様によく選ばれているオプションです。",
    collapse: "閉じる",
    currentSelection: "現在の選択",
    next: "次へ",
    selectThisProduct: "この商品を選択",
    completeSelection: "選択を完了",
    logoRemovalCleanTitle: "ロゴなし高解像度オリジナル",
    logoRemovalWithLogo: "ロゴあり",
    logoRemovalWithoutLogo: "ロゴなし",
    logoRemovalComparisonDescription: "写真内のLAFIAロゴを削除したオリジナルデータを提供します。",
    logoRemovalUseTitle: "広告・ショップ・SNSですぐ使えます",
    logoRemovalUseDescription: "ロゴ削除ファイルと商用利用許可をセットで提供します。",
    logoRemovalSns: "SNS",
    logoRemovalOnline: "オンラインショップ",
    logoRemovalPromo: "販促物",
    conceptAddonTitle: "一度のご来店で、二つのプリンセスコンセプト",
    selectedConceptLabel: "選択したコンセプト",
    otherPremiumConceptLabel: "別のプレミアムコンセプト",
    conceptAddonDescription: "選択した商品の特典はそのまま、もう一つのコンセプトを撮影",
    retouchAddonTitle: "レタッチ写真をもう1枚",
    retouchAddonClosedPose: "目を閉じた優雅なポーズ",
    retouchAddonSmilePose: "カメラに向けた明るい笑顔",
    retouchAddonDescription: "同じコンセプトで、別ポーズのレタッチデータを追加します。",
  },
}

export default {
  name: "BookingUiV2",
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    lang: {
      type: String,
      default: "ko",
    },
    concepts: {
      type: Array,
      default: () => [],
    },
    selectedConcept: {
      type: Object,
      default: null,
    },
    conceptLocked: {
      type: Boolean,
      default: false,
    },
    productType: {
      type: String,
      default: "",
    },
    showStaff: {
      type: Boolean,
      default: false,
    },
    staffList: {
      type: Array,
      default: () => [],
    },
    selectedStaffId: {
      type: [Number, String],
      default: null,
    },
    staffLoading: {
      type: Boolean,
      default: false,
    },
    date: {
      type: [Object, String],
      default: null,
    },
    availableTimes: {
      type: Array,
      default: () => [],
    },
    reservedTimes: {
      type: Array,
      default: () => [],
    },
    selectedTime: {
      type: String,
      default: "",
    },
    timeLoading: {
      type: Boolean,
      default: false,
    },
    staffHolidayInfo: {
      type: Object,
      default: () => ({ isHoliday: false, comment: null }),
    },
    isPastDate: {
      type: Boolean,
      default: false,
    },
    isSameDayBlocked: {
      type: Boolean,
      default: false,
    },
    isOutsidePromotionPeriod: {
      type: Boolean,
      default: false,
    },
    products: {
      type: Array,
      default: () => [],
    },
    selectedProduct: {
      type: Object,
      default: null,
    },
    persons: {
      type: Number,
      default: 1,
    },
    optionProducts: {
      type: Array,
      default: () => [],
    },
    selectedOptionProducts: {
      type: Array,
      default: () => [],
    },
    totalPrice: {
      type: Number,
      default: 0,
    },
    canPay: {
      type: Boolean,
      default: false,
    },
    paymentPreviewOnly: {
      type: Boolean,
      default: false,
    },
    payLoading: {
      type: Boolean,
      default: false,
    },
    localSafetyPreview: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      activeSheet: "",
      bodyLocked: false,
      lockedScrollY: 0,
      savedBodyStyles: null,
      previousFocus: null,
      outsideContentState: [],
      imageErrors: {},
      logoRemovalWithLogoImage,
      logoRemovalWithoutLogoImage,
      logoRemovalUseCasesImage,
      retouchAddClosedEyesImage,
      retouchAddToothySmileImage,
      shootingModeThumbnails: SHOOTING_MODE_THUMBNAILS,
      productFlowStage: "mode",
      draftBookingMode: "",
      draftProductTier: "",
      draftPatternCount: 1,
      draftPersons: 0,
      draftConfirmedProduct: null,
      activeOptionDetailId: null,
      addonPremiumPreview: null,
      productFlowDirection: "forward",
      recentlyUpdatedSection: "",
      feedbackReady: false,
      feedbackTimer: null,
      detailSwitchTimer: null,
      staffDetailTimer: null,
      summaryScrollTimer: null,
      checkoutVisible: false,
      checkoutVisibilityFrame: null,
      checkoutVisibilityObserver: null,
      checkoutLayoutObserver: null,
      checkoutVisibilityTimers: [],
    }
  },
  computed: {
    selectionComplete() {
      return Boolean(this.selectedTime !== "" && this.persons && this.selectedConcept && this.selectedProduct && (!this.showStaff || this.selectedStaffId))
    },
    paymentButtonEnabled() {
      return Boolean(this.canPay || (this.paymentPreviewOnly && this.selectionComplete))
    },
    productFlowTransitionName() {
      return this.productFlowDirection === "backward" ? "booking-product-backward" : "booking-product-forward"
    },
    productFlowHeadingTransitionName() {
      return this.productFlowDirection === "backward" ? "booking-heading-backward" : "booking-heading-forward"
    },
    flowActionLabel() {
      if (this.productFlowStage === "pattern") {
        return this.copy.selectThisProduct
      }
      if (this.productFlowStage === "addons") {
        return this.copy.completeSelection
      }
      return this.copy.next
    },
    flowActionDisabled() {
      if (this.productFlowStage === "mode") {
        return !this.canAdvanceMode
      }
      if (this.productFlowStage === "tier") {
        return !this.draftProductTier
      }
      if (this.productFlowStage === "pattern") {
        return !this.selectedDraftPatternProduct
      }
      return false
    },
    languageTag() {
      return this.lang === "jp" ? "ja" : this.lang
    },
    copy() {
      return COPY[this.lang] || COPY.ko
    },
    nativeDateValue() {
      if (!this.date) {
        return ""
      }
      if (typeof this.date.format === "function") {
        return this.date.format("YYYY-MM-DD")
      }
      const parsed = this.dayjs(this.date)
      return parsed && parsed.isValid() ? parsed.format("YYYY-MM-DD") : ""
    },
    nativeDateMinimum() {
      return this.dayjs().add(1, "day").format("YYYY-MM-DD")
    },
    displayConcepts() {
      if (!this.productType) {
        return this.concepts
      }
      const matched = this.concepts.filter((concept) => !concept.productType || concept.productType === this.productType)
      return matched.length ? matched : this.concepts
    },
    patternImages() {
      if (!this.selectedConcept) {
        return []
      }

      const patterns = []
      if (this.selectedConcept.pattern_1_image) {
        patterns.push({ id: "primary", image: this.selectedConcept.pattern_1_image, order: -1 })
      }

      let others = this.selectedConcept.pattern_others_image || []
      if (typeof others === "string") {
        try {
          others = JSON.parse(others)
        } catch (error) {
          others = []
        }
      }

      if (Array.isArray(others)) {
        others
          .filter((pattern) => pattern && pattern.image)
          .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0))
          .forEach((pattern, index) => {
            patterns.push({
              id: `${pattern.id || "other"}-${index}`,
              image: pattern.image,
              order: Number(pattern.order) || index,
            })
          })
      }

      return patterns.map((pattern, index) => ({
        ...pattern,
        label: this.patternLabel(index),
      }))
    },
    isProductFlowSheet() {
      return this.activeSheet === "product" || this.activeSheet === "option"
    },
    showSheetBack() {
      return this.activeSheet === "product" && ["tier", "pattern", "addons"].includes(this.productFlowStage)
    },
    soloProducts() {
      return this.products.filter((product) => product && product.type !== "group")
    },
    groupProducts() {
      return this.products.filter((product) => product && product.type === "group")
    },
    modeProducts() {
      return this.draftBookingMode === "group" ? this.groupProducts : this.soloProducts
    },
    tierChoices() {
      return ["gold", "basic"]
        .map((tier) => ({
          tier,
          product:
            this.modeProducts.find((product) => this.productTier(product) === tier && this.patternCountForProduct(product) === 1) ||
            this.modeProducts.find((product) => this.productTier(product) === tier),
        }))
        .filter((choice) => choice.product)
    },
    availablePatternProducts() {
      return this.modeProducts
        .filter((product) => this.productTier(product) === this.draftProductTier)
        .map((product) => ({ product, count: this.patternCountForProduct(product) || 1 }))
        .filter((choice) => choice.count > 0)
        .sort((a, b) => a.count - b.count)
    },
    selectedDraftPatternProduct() {
      const choice = this.availablePatternProducts.find((item) => item.count === this.draftPatternCount)
      return choice ? choice.product : null
    },
    groupMaximumPersons() {
      if (!this.groupProducts.length) {
        return 6
      }
      return this.groupProducts.reduce((maximum, product) => Math.max(maximum, this.maxPersons(product)), 2)
    },
    canAdvanceMode() {
      return this.draftBookingMode === "solo" || (this.draftBookingMode === "group" && this.draftPersons >= 2)
    },
    isGroupPriceMode() {
      if (this.draftBookingMode) {
        return this.draftBookingMode === "group"
      }
      return Boolean(this.flowSelectedProduct && this.flowSelectedProduct.type === "group")
    },
    productFlowPersons() {
      if (!this.isGroupPriceMode) {
        return 1
      }
      return Math.max(Number(this.draftPersons) || Number(this.persons) || 2, 2)
    },
    productFlowPriceProduct() {
      if (this.productFlowStage === "mode") {
        return null
      }
      if (this.productFlowStage === "tier") {
        const choice = this.tierChoices.find((item) => item.tier === this.draftProductTier)
        return choice ? choice.product : null
      }
      if (this.productFlowStage === "pattern") {
        return this.selectedDraftPatternProduct
      }
      return this.flowSelectedProduct
    },
    hasProductFlowPrice() {
      return Boolean(this.productFlowPriceProduct)
    },
    productFlowTotalPrice() {
      if (!this.productFlowPriceProduct) {
        return 0
      }
      const basePrice = this.flowProductTotal(this.productFlowPriceProduct)
      if (this.productFlowStage !== "addons") {
        return basePrice
      }
      return this.selectedOptionProducts.reduce((total, option) => {
        const quantity = Number(option.quantity) || 1
        return total + this.flowOptionPrice(option) * quantity
      }, basePrice)
    },
    flowSelectedProduct() {
      return this.draftConfirmedProduct || this.selectedProduct
    },
    productPreviewImage() {
      if (this.patternImages.length) {
        return this.patternImage(this.patternImages[0].image)
      }
      return this.conceptImage(this.selectedConcept)
    },
    goldPrintPreviewImages() {
      const image = this.productPreviewImage
      if (!image) {
        return []
      }
      return [image, image]
    },
    logoRemovalUses() {
      return [
        { key: "sns", label: this.copy.logoRemovalSns },
        { key: "online", label: this.copy.logoRemovalOnline },
        { key: "promo", label: this.copy.logoRemovalPromo },
      ]
    },
    visibleAddonOptions() {
      const mode = this.flowSelectedProduct && this.flowSelectedProduct.type === "group" ? "group" : "solo"
      const tier = this.productTier(this.flowSelectedProduct)
      const plainOptions = []
      const conditionalOptions = new Map()

      this.optionProducts.forEach((product, index) => {
        if (this.isPatternOptionProduct(product)) {
          return
        }
        const rule = this.optionDisplayRule(product)
        if (!rule.family) {
          plainOptions.push({ index, product })
          return
        }
        if (!conditionalOptions.has(rule.family)) {
          conditionalOptions.set(rule.family, [])
        }
        conditionalOptions.get(rule.family).push({ index, product, rule })
      })

      conditionalOptions.forEach((candidates) => {
        const matched = candidates
          .filter(({ rule }) => (!rule.mode || rule.mode === mode) && (!rule.tier || rule.tier === tier))
          .sort((left, right) => this.optionRuleScore(right.rule, mode, tier) - this.optionRuleScore(left.rule, mode, tier))
        if (matched.length) {
          plainOptions.push({ index: Math.min(...candidates.map((item) => item.index)), product: matched[0].product })
        }
      })

      return plainOptions.sort((left, right) => left.index - right.index).map((item) => item.product)
    },
    selectedStaff() {
      return this.staffList.find((staff) => String(staff.id) === String(this.selectedStaffId)) || null
    },
    scheduleDisabled() {
      return !this.selectedConcept || (this.showStaff && !this.selectedStaffId)
    },
    scheduleSummary() {
      if (!this.date || !this.selectedTime) {
        return this.copy.chooseSchedule
      }
      return `${this.formatDate(this.date)} · ${this.selectedTime}`
    },
    productSummary() {
      if (!this.selectedProduct) {
        return this.copy.chooseProduct
      }
      const people = this.selectedProduct.type === "group" ? ` · ${this.persons}${this.lang === "en" ? " " : ""}${this.copy.peopleUnit}` : ""
      return `${this.productTitle(this.selectedProduct)}${people}`
    },
    optionSummary() {
      if (!this.selectedOptionProducts.length) {
        return this.copy.chooseOption
      }
      if (this.selectedOptionProducts.length === 1) {
        return this.optionTitle(this.selectedOptionProducts[0])
      }
      if (this.lang === "en") {
        return `${this.selectedOptionProducts.length} selected`
      }
      if (this.lang === "jp") {
        return `${this.selectedOptionProducts.length}件選択`
      }
      return `${this.selectedOptionProducts.length}개 선택`
    },
    progressSteps() {
      const steps = [
        { key: "concept", label: this.copy.concept, complete: !!this.selectedConcept },
        ...(this.showStaff ? [{ key: "staff", label: this.copy.staff, complete: !!this.selectedStaffId }] : []),
        { key: "schedule", label: this.copy.schedule, complete: !!this.selectedTime },
        { key: "product", label: this.copy.product, complete: !!this.selectedProduct },
      ]
      return steps
    },
    currentStepIndex() {
      const firstIncomplete = this.progressSteps.findIndex((step) => !step.complete)
      return firstIncomplete === -1 ? this.progressSteps.length - 1 : firstIncomplete
    },
    progressLabel() {
      const completeCount = this.progressSteps.filter((step) => step.complete).length
      return `${this.copy.progress}: ${completeCount}/${this.progressSteps.length}`
    },
    activeSheetTitle() {
      if (this.isProductFlowSheet) {
        if (this.productFlowStage === "pattern") {
          return this.copy.patternCountTitle
        }
        if (this.productFlowStage === "addons") {
          return this.copy.addOnTitle
        }
        return this.copy.productSelectTitle
      }
      return {
        pattern: this.copy.conceptDetail,
        concept: this.copy.concept,
        staff: this.copy.staffDialogTitle,
        schedule: this.copy.scheduleDialogTitle,
        product: this.copy.product,
        option: this.copy.option,
      }[this.activeSheet]
    },
    activeSheetNumber() {
      if (this.isProductFlowSheet) {
        if (this.productFlowStage === "mode") {
          return "1 / 4"
        }
        if (this.productFlowStage === "tier") {
          return "2 / 4"
        }
        if (this.productFlowStage === "pattern") {
          return "3 / 4"
        }
        if (this.productFlowStage === "addons") {
          return "4 / 4"
        }
        return ""
      }
      const number = {
        concept: "01",
        staff: "02",
        schedule: this.showStaff ? "03" : "02",
        product: this.showStaff ? "04" : "03",
        option: this.showStaff ? "05" : "04",
      }[this.activeSheet]
      return number ? `STEP ${number}` : ""
    },
    sheetTitleId() {
      return `booking-v2-sheet-title-${this.activeSheet}`
    },
    scheduleMessage() {
      if (this.staffHolidayInfo && this.staffHolidayInfo.isHoliday) {
        return this.staffHolidayInfo.comment || this.copy.staffHoliday
      }
      if (this.isPastDate) {
        return this.copy.pastDate
      }
      if (this.isSameDayBlocked) {
        return this.copy.sameDay
      }
      if (this.isOutsidePromotionPeriod) {
        return this.copy.outsidePeriod
      }
      if (this.showStaff && !this.selectedStaffId) {
        return this.copy.selectStaffFirst
      }
      if (!this.availableTimes.length) {
        return this.copy.noTimes
      }
      return ""
    },
  },
  watch: {
    selectedConcept(next, previous) {
      if (next && String(next.id || "") !== String((previous && previous.id) || "")) {
        this.scrollSummaryIntoView(this.showStaff ? "staff" : "schedule")
      }
    },
    selectedStaffId(next, previous) {
      if (this.feedbackReady && String(next || "") !== String(previous || "") && next && !this.activeSheet) {
        this.flashUpdatedSection("staff")
        this.scrollSummaryIntoView("schedule")
      }
    },
    selectedTime(next, previous) {
      if (next && next !== previous) {
        this.scrollSummaryIntoView("product")
      }
    },
    selectedProduct(next, previous) {
      if (next && String(next.id || "") !== String((previous && previous.id) || "")) {
        this.scrollSummaryIntoView(this.optionProducts.length ? "option" : "product")
      }
    },
  },
  mounted() {
    document.body.classList.add("booking-ui-v2-active")
    this.$nextTick(() => {
      this.feedbackReady = true
      this.startCheckoutVisibilityTracking()
    })
  },
  beforeDestroy() {
    this.stopCheckoutVisibilityTracking()
    this.unlockBody()
    this.restoreOutsideContent()
    document.body.classList.remove("booking-ui-v2-active")
    document.removeEventListener("keydown", this.handleKeydown)
    window.clearTimeout(this.feedbackTimer)
    window.clearTimeout(this.detailSwitchTimer)
    window.clearTimeout(this.staffDetailTimer)
    window.clearTimeout(this.summaryScrollTimer)
  },
  methods: {
    startCheckoutVisibilityTracking() {
      this.stopCheckoutVisibilityTracking()
      this.checkoutVisible = false
      if ("IntersectionObserver" in window) {
        this.checkoutVisibilityObserver = new window.IntersectionObserver(
          (entries) => {
            const entry = entries.find((item) => item.target === this.$el)
            if (entry) {
              this.checkoutVisible = entry.isIntersecting && entry.boundingClientRect.bottom > 0
            }
          },
          { rootMargin: "0px 0px -96px 0px", threshold: 0 }
        )
        this.checkoutVisibilityObserver.observe(this.$el)
      }
      if ("ResizeObserver" in window) {
        this.checkoutLayoutObserver = new window.ResizeObserver(this.scheduleCheckoutVisibilityUpdate)
        this.checkoutLayoutObserver.observe(document.body)
      }
      window.addEventListener("scroll", this.scheduleCheckoutVisibilityUpdate, { passive: true })
      window.addEventListener("resize", this.scheduleCheckoutVisibilityUpdate, { passive: true })
      window.addEventListener("load", this.scheduleCheckoutVisibilityUpdate)
      this.checkoutVisibilityTimers = [250, 1000, 2500].map((delay) =>
        window.setTimeout(this.scheduleCheckoutVisibilityUpdate, delay)
      )
      this.scheduleCheckoutVisibilityUpdate()
    },
    stopCheckoutVisibilityTracking() {
      window.removeEventListener("scroll", this.scheduleCheckoutVisibilityUpdate)
      window.removeEventListener("resize", this.scheduleCheckoutVisibilityUpdate)
      window.removeEventListener("load", this.scheduleCheckoutVisibilityUpdate)
      if (this.checkoutVisibilityObserver) {
        this.checkoutVisibilityObserver.disconnect()
        this.checkoutVisibilityObserver = null
      }
      if (this.checkoutLayoutObserver) {
        this.checkoutLayoutObserver.disconnect()
        this.checkoutLayoutObserver = null
      }
      this.checkoutVisibilityTimers.forEach((timer) => window.clearTimeout(timer))
      this.checkoutVisibilityTimers = []
      if (this.checkoutVisibilityFrame !== null) {
        window.cancelAnimationFrame(this.checkoutVisibilityFrame)
        this.checkoutVisibilityFrame = null
      }
    },
    scheduleCheckoutVisibilityUpdate() {
      if (this.checkoutVisibilityFrame !== null) {
        return
      }
      this.checkoutVisibilityFrame = window.requestAnimationFrame(() => {
        this.checkoutVisibilityFrame = null
        this.updateCheckoutVisibility()
      })
    },
    updateCheckoutVisibility() {
      const root = this.$el
      if (!root) {
        this.checkoutVisible = false
        return
      }
      const rect = root.getBoundingClientRect()
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0
      this.checkoutVisible = rect.top <= Math.max(0, viewportHeight - 96) && rect.bottom > 0
    },
    handlePayClick() {
      if (this.paymentPreviewOnly || !this.canPay) {
        return
      }
      this.$emit("pay")
    },
    initializeProductFlow(sheet) {
      this.productFlowDirection = "forward"
      this.activeOptionDetailId = null
      this.addonPremiumPreview = null
      this.draftConfirmedProduct = this.selectedProduct

      if (sheet === "option") {
        this.productFlowStage = "addons"
        return
      }

      this.productFlowStage = "mode"
      this.draftBookingMode = ""
      this.draftProductTier = ""
      this.draftPatternCount = 1
      this.draftPersons = 0
    },
    openSheet(sheet, triggerElement) {
      if (this.loading || this.activeSheet || (sheet === "concept" && this.conceptLocked)) {
        return
      }
      if (sheet === "pattern" && !this.selectedConcept) {
        return
      }
      if (sheet === "staff" && !this.selectedConcept) {
        return
      }
      if (sheet === "schedule" && this.scheduleDisabled && !this.localSafetyPreview) {
        return
      }
      if (sheet === "product" && !this.selectedTime && !this.localSafetyPreview) {
        return
      }
      if (sheet === "option" && !this.selectedProduct) {
        return
      }

      this.previousFocus = triggerElement instanceof HTMLElement ? triggerElement : document.activeElement
      if (sheet === "product" || sheet === "option") {
        this.initializeProductFlow(sheet)
      }
      this.activeSheet = sheet
      this.lockBody()
      this.disableOutsideContent()
      document.addEventListener("keydown", this.handleKeydown)
      this.$nextTick(() => {
        if (this.$refs.closeButton) {
          this.$refs.closeButton.focus()
        }
      })
    },
    closeSheet(feedbackSection = "") {
      if (!this.activeSheet) {
        return
      }
      const section = typeof feedbackSection === "string" ? feedbackSection : ""
      this.activeSheet = ""
      document.removeEventListener("keydown", this.handleKeydown)
      this.restoreOutsideContent()
      this.unlockBody()
      this.$nextTick(() => {
        if (this.previousFocus && typeof this.previousFocus.focus === "function") {
          this.previousFocus.focus()
        }
        this.previousFocus = null
      })
      if (section) {
        window.clearTimeout(this.feedbackTimer)
        this.feedbackTimer = window.setTimeout(() => this.flashUpdatedSection(section), 220)
        if (section === "product") {
          this.scrollSummaryIntoView(this.optionProducts.length ? "option" : "product")
        }
      }
    },
    handleKeydown(event) {
      if (event.key === "Escape") {
        this.closeSheet()
        return
      }
      if (event.key === "Tab" && this.activeSheet) {
        this.trapSheetFocus(event)
      }
    },
    focusableSheetElements() {
      const sheet = this.$refs.sheet
      if (!sheet) {
        return []
      }
      const selector = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      return Array.from(sheet.querySelectorAll(selector)).filter((element) => element.getClientRects().length > 0 && element.getAttribute("aria-hidden") !== "true")
    },
    trapSheetFocus(event) {
      const focusable = this.focusableSheetElements()
      if (!focusable.length) {
        event.preventDefault()
        if (this.$refs.sheet) {
          this.$refs.sheet.focus()
        }
        return
      }
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      const active = document.activeElement
      if (!this.$refs.sheet.contains(active)) {
        event.preventDefault()
        first.focus()
        return
      }
      if (event.shiftKey && active === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      }
    },
    disableOutsideContent() {
      this.restoreOutsideContent()
      const targets = new Set([this.$refs.pageShell, this.$refs.checkout].filter(Boolean))
      let current = this.$el
      let parent = current && current.parentElement
      while (parent) {
        Array.from(parent.children).forEach((child) => {
          if (child !== current) {
            targets.add(child)
          }
        })
        if (parent === document.body) {
          break
        }
        current = parent
        parent = parent.parentElement
      }

      this.outsideContentState = Array.from(targets).map((element) => ({
        element,
        hadInert: element.hasAttribute("inert"),
        ariaHidden: element.getAttribute("aria-hidden"),
      }))
      this.outsideContentState.forEach(({ element }) => {
        element.setAttribute("inert", "")
        element.setAttribute("aria-hidden", "true")
      })
    },
    restoreOutsideContent() {
      this.outsideContentState.forEach(({ element, hadInert, ariaHidden }) => {
        if (!element || !element.removeAttribute) {
          return
        }
        if (!hadInert) {
          element.removeAttribute("inert")
        }
        if (ariaHidden === null) {
          element.removeAttribute("aria-hidden")
        } else {
          element.setAttribute("aria-hidden", ariaHidden)
        }
      })
      this.outsideContentState = []
    },
    goBackInSheet() {
      if (this.productFlowStage === "addons") {
        this.setProductFlowStage("pattern", "backward")
        return
      }
      if (this.productFlowStage === "pattern") {
        this.setProductFlowStage("tier", "backward")
        return
      }
      this.setProductFlowStage("mode", "backward")
    },
    selectDraftBookingMode(mode) {
      if (!["solo", "group"].includes(mode)) {
        return
      }
      this.draftBookingMode = mode
      this.draftProductTier = ""
      this.draftPatternCount = 1
      this.draftPersons = mode === "solo" ? 1 : 0
    },
    advanceFromMode() {
      if (!this.canAdvanceMode) {
        return
      }
      this.setProductFlowStage("tier", "forward")
    },
    advanceFromTier() {
      if (!this.draftProductTier) {
        return
      }
      this.setProductFlowStage("pattern", "forward")
    },
    setProductFlowStage(stage, direction) {
      this.productFlowDirection = direction === "backward" ? "backward" : "forward"
      this.productFlowStage = stage
      this.$nextTick(() => {
        if (this.$refs.sheetBody) {
          this.$refs.sheetBody.scrollTop = 0
        }
      })
    },
    handleProductFlowAction() {
      if (this.flowActionDisabled) {
        return
      }
      if (this.productFlowStage === "mode") {
        this.advanceFromMode()
        return
      }
      if (this.productFlowStage === "tier") {
        this.advanceFromTier()
        return
      }
      if (this.productFlowStage === "pattern") {
        this.confirmDraftProduct()
        return
      }
      this.closeSheet(this.activeSheet === "option" ? "option" : "product")
    },
    selectDraftProductTier(tier) {
      this.draftProductTier = tier
    },
    selectDraftPatternCount(count) {
      this.draftPatternCount = count
    },
    flashUpdatedSection(section) {
      window.clearTimeout(this.feedbackTimer)
      this.recentlyUpdatedSection = section
      this.feedbackTimer = window.setTimeout(() => {
        this.recentlyUpdatedSection = ""
      }, 400)
    },
    lockBody() {
      if (this.bodyLocked) {
        return
      }
      const body = document.body
      this.lockedScrollY = window.scrollY || window.pageYOffset || 0
      this.savedBodyStyles = {
        position: body.style.position,
        top: body.style.top,
        left: body.style.left,
        right: body.style.right,
        width: body.style.width,
        overflow: body.style.overflow,
      }
      body.style.position = "fixed"
      body.style.top = `-${this.lockedScrollY}px`
      body.style.left = "0"
      body.style.right = "0"
      body.style.width = "100%"
      body.style.overflow = "hidden"
      this.bodyLocked = true
    },
    unlockBody() {
      if (!this.bodyLocked || !this.savedBodyStyles) {
        return
      }
      const body = document.body
      Object.keys(this.savedBodyStyles).forEach((key) => {
        body.style[key] = this.savedBodyStyles[key]
      })
      window.scrollTo(0, this.lockedScrollY)
      this.bodyLocked = false
      this.savedBodyStyles = null
    },
    selectConcept(concept) {
      this.$emit("select-concept", concept)
      this.closeSheet("concept")
    },
    selectStaff(staff) {
      this.$emit("select-staff", staff.id)
      this.closeSheet()
    },
    showStaffDetail(staff) {
      this.closeSheet()
      window.clearTimeout(this.staffDetailTimer)
      this.staffDetailTimer = window.setTimeout(() => this.$emit("staff-detail", staff), 180)
    },
    selectTime(time) {
      if (this.reservedTimes.includes(time)) {
        return
      }
      this.$emit("select-time", time)
      this.closeSheet("schedule")
    },
    handleDateUpdated(date) {
      if (!this.localSafetyPreview) {
        this.$emit("date-updated", date)
      }
    },
    shiftBookingDate(change) {
      const current = this.date && typeof this.date.add === "function" ? this.date : this.dayjs(this.date || undefined)
      const next = current.add(change, "day").startOf("day")
      if (next.isBefore(this.dayjs(this.nativeDateMinimum), "day")) {
        return
      }
      this.handleDateUpdated(next)
    },
    handleNativeDateInput(event) {
      const value = event && event.target ? event.target.value : ""
      const next = this.dayjs(value)
      if (!value || !next.isValid() || next.isBefore(this.dayjs(this.nativeDateMinimum), "day")) {
        return
      }
      this.handleDateUpdated(next.startOf("day"))
    },
    selectProduct(product) {
      const count = product.type === "group" ? (this.selectedProduct && this.selectedProduct.id === product.id ? Math.max(this.persons, 2) : 2) : undefined
      this.$emit("select-product", product, count)
      this.closeSheet()
    },
    productTier(product) {
      const registeredTier = String(product && (product.tier || product.grade || product.productTier) ? product.tier || product.grade || product.productTier : "").toLowerCase()
      if (/gold|골드|ゴールド/.test(registeredTier)) {
        return "gold"
      }
      if (/basic|베이직|ベーシック/.test(registeredTier)) {
        return "basic"
      }
      const text = `${this.productTitle(product)} ${this.productSubtitle(product)}`.toLowerCase()
      return /gold|골드|ゴールド|당일|当日|print|인화|プリント/.test(text) ? "gold" : "basic"
    },
    isGoldProduct(product) {
      return this.productTier(product) === "gold"
    },
    patternCountForProduct(product) {
      const value = Number(product && (product.patternCount || product.pattern_count))
      if (value > 0) {
        return value
      }

      const text = `${this.productTitle(product)} ${this.productSubtitle(product)}`
      const registeredPatternCount = text.match(/(\d+)\s*(?:패턴|patterns?|パターン)/i)
      if (registeredPatternCount) {
        return Number(registeredPatternCount[1]) || 1
      }

      const registeredMultiplier = text.match(/[x×]\s*(\d+)/i)
      return registeredMultiplier ? Number(registeredMultiplier[1]) || 1 : 1
    },
    patternCountText(count) {
      if (this.lang === "en") {
        return `${count} ${count === 1 ? "pattern" : "patterns"}`
      }
      if (this.lang === "jp") {
        return `${count}パターン`
      }
      return `${count}패턴`
    },
    patternPreviewImages(count) {
      const images = this.patternImages.slice(0, count).map((pattern) => this.patternImage(pattern.image)).filter(Boolean)
      if (!images.length && this.productPreviewImage) {
        return [this.productPreviewImage]
      }
      return images
    },
    clearPatternOptionSelections() {
      this.selectedOptionProducts.filter((product) => this.isPatternOptionProduct(product)).forEach((product) => {
        this.$emit("toggle-option", { product, selected: false, quantity: 1 })
      })
    },
    isPatternOptionProduct(product) {
      const code = String(product && product.code ? product.code : "").toLowerCase()
      return code === "add_pattern"
    },
    confirmDraftProduct() {
      if (!this.selectedDraftPatternProduct) {
        return
      }
      this.clearPatternOptionSelections()
      this.draftConfirmedProduct = this.selectedDraftPatternProduct
      if (this.draftBookingMode === "group") {
        this.$emit("select-product", this.selectedDraftPatternProduct, this.draftPersons)
      } else {
        this.$emit("select-product", this.selectedDraftPatternProduct)
      }
      this.syncSelectedOptionsForMode()
      this.setProductFlowStage("addons", "forward")
    },
    changeDraftPersons(change) {
      if (this.draftBookingMode !== "group") {
        return
      }
      if (this.draftPersons < 2) {
        this.draftPersons = change > 0 ? 2 : 0
        return
      }
      this.draftPersons = Math.min(this.groupMaximumPersons, Math.max(2, this.draftPersons + change))
    },
    changePersons(product, change) {
      const next = Math.min(this.maxPersons(product), Math.max(2, this.persons + change))
      this.$emit("select-product", product, next)
    },
    maxPersons(product) {
      return Number(product.maxpersons) || 6
    },
    isOptionSelected(productId) {
      return this.selectedOptionProducts.some((product) => String(product.id) === String(productId))
    },
    selectedOption(productId) {
      return this.selectedOptionProducts.find((product) => String(product.id) === String(productId)) || null
    },
    optionQuantity(productId) {
      const selected = this.selectedOption(productId)
      return selected ? Number(selected.quantity) || 1 : 1
    },
    maxOptionQuantity(product) {
      return Number(product.maxCount) || 19
    },
    flowProductTotal(product) {
      const price = Math.max(Number(product && product.price) || 0, 0)
      return product && product.type === "group" ? price * this.productFlowPersons : price
    },
    groupPriceBreakdown(product) {
      const price = this.formatMoney(Math.max(Number(product && product.price) || 0, 0))
      if (this.lang === "en") {
        return `${price}${this.copy.currency} × ${this.productFlowPersons}`
      }
      if (this.lang === "jp") {
        return `1名 ${price}${this.copy.currency} × ${this.productFlowPersons}名`
      }
      return `1인 ${price}${this.copy.currency} × ${this.productFlowPersons}명`
    },
    flowOptionPrice(product) {
      return Math.max(Number(product && product.price) || 0, 0)
    },
    optionDisplayRule(product) {
      const code = String(product && product.code ? product.code : "").trim().toLowerCase()
      const descriptionCode = String(product && product.descriptionCode ? product.descriptionCode : "").trim().toLowerCase()
      let family = ""
      if (code.startsWith("concept_") || descriptionCode === "op_addcon" || descriptionCode === "op_addcon2") {
        family = "concept"
      } else if (code.startsWith("retouch") || descriptionCode === "op_ret") {
        family = "retouch"
      }
      const mode = /(?:^|_)group(?:_|\+)/.test(code) ? "group" : /(?:^|_)solo(?:_|\+)/.test(code) ? "solo" : ""
      const tier = /(?:^|_)gold(?:_|\+)/.test(code) ? "gold" : /(?:^|_)basic(?:_|\+)/.test(code) ? "basic" : ""
      return { family, mode, tier }
    },
    optionRuleScore(rule, mode, tier) {
      let score = 0
      if (rule.mode === mode) {
        score += 4
      }
      if (rule.tier === tier) {
        score += 2
      }
      if (!rule.mode) {
        score += 1
      }
      return score
    },
    syncSelectedOptionsForMode() {
      const visibleIds = new Set(this.visibleAddonOptions.map((product) => String(product.id)))
      this.selectedOptionProducts.filter((product) => {
        const rule = this.optionDisplayRule(product)
        return rule.family && !visibleIds.has(String(product.id))
      }).forEach((product) => {
        this.$emit("toggle-option", {
          product,
          selected: false,
          quantity: Number(product.quantity) || 1,
        })
      })
    },
    toggleOption(product) {
      const selected = this.isOptionSelected(product.id)
      this.$emit("toggle-option", {
        product,
        selected: !selected,
        quantity: selected ? 1 : this.optionQuantity(product.id),
      })
    },
    toggleOptionDetail(product) {
      const productId = product && product.id
      if (String(this.activeOptionDetailId) === String(productId)) {
        this.activeOptionDetailId = null
        return
      }
      const openDetail = () => {
        if (this.isConceptAddonOption(product)) {
          this.chooseAddonPremiumPreview()
        }
        this.activeOptionDetailId = productId
      }
      window.clearTimeout(this.detailSwitchTimer)
      if (this.activeOptionDetailId !== null) {
        this.activeOptionDetailId = null
        this.detailSwitchTimer = window.setTimeout(openDetail, 220)
        return
      }
      openDetail()
    },
    beforeAddonDetailEnter(element) {
      element.style.height = "0"
      element.style.opacity = "0"
      element.style.transform = "translateY(8px)"
    },
    enterAddonDetail(element) {
      const targetHeight = element.scrollHeight
      window.requestAnimationFrame(() => {
        element.style.height = `${targetHeight}px`
        element.style.opacity = "1"
        element.style.transform = "translateY(0)"
      })
    },
    afterAddonDetailEnter(element) {
      element.style.height = "auto"
      element.style.opacity = ""
      element.style.transform = ""
      this.keepAddonDetailVisible(element)
    },
    beforeAddonDetailLeave(element) {
      element.style.height = `${element.scrollHeight}px`
      element.style.opacity = "1"
      element.style.transform = "translateY(0)"
    },
    leaveAddonDetail(element) {
      window.requestAnimationFrame(() => {
        element.style.height = "0"
        element.style.opacity = "0"
        element.style.transform = "translateY(8px)"
      })
    },
    afterAddonDetailLeave(element) {
      element.style.height = ""
      element.style.opacity = ""
      element.style.transform = ""
    },
    keepAddonDetailVisible(element) {
      const body = this.$refs.sheetBody
      const item = element.closest(".booking-v2__addon-choice")
      if (!body || !item) {
        return
      }
      const bodyRect = body.getBoundingClientRect()
      const itemRect = item.getBoundingClientRect()
      if (itemRect.bottom <= bodyRect.bottom - 8 && itemRect.top >= bodyRect.top + 8) {
        return
      }
      const nextTop = body.scrollTop + itemRect.top - bodyRect.top - 8
      const reduceMotion = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches
      body.scrollTo({ top: Math.max(0, nextTop), behavior: reduceMotion ? "auto" : "smooth" })
    },
    optionSubtitle(product) {
      return this.localizedValue(product && (product.subTitle || product.subtitle))
    },
    isLogoRemovalOption(product) {
      const code = String(product && product.code ? product.code : "").trim().toLowerCase()
      const descriptionCode = String(product && product.descriptionCode ? product.descriptionCode : "").trim().toLowerCase()
      return code === "logo_removal" || descriptionCode === "op_logoli"
    },
    isConceptAddonOption(product) {
      return this.optionDisplayRule(product).family === "concept"
    },
    isRetouchAddonOption(product) {
      return this.optionDisplayRule(product).family === "retouch"
    },
    conceptShotImages(concept) {
      if (!concept) {
        return []
      }
      let others = concept.pattern_others_image || []
      if (typeof others === "string") {
        try {
          others = JSON.parse(others)
        } catch (error) {
          others = []
        }
      }
      const imageNames = [concept.pattern_1_image]
      if (Array.isArray(others)) {
        others.forEach((pattern) => imageNames.push(pattern && pattern.image))
      }
      imageNames.push(concept.thumbnail_image)
      return imageNames.filter(Boolean).map((image) => this.patternImage(image))
    },
    premiumPreviewCandidates() {
      const selectedId = String(this.selectedConcept && this.selectedConcept.id ? this.selectedConcept.id : "")
      const selectedProductType = String((this.selectedConcept && this.selectedConcept.productType) || this.productType || "").toLowerCase()
      const seenImages = new Set([this.productPreviewImage].filter(Boolean))
      const candidates = []

      this.displayConcepts.forEach((concept) => {
        const conceptId = String(concept && concept.id ? concept.id : "")
        const conceptProductType = String((concept && concept.productType) || selectedProductType).toLowerCase()
        if (!concept || conceptId === selectedId || conceptProductType !== "premium") {
          return
        }
        this.conceptShotImages(concept).forEach((image, index) => {
          if (!image || seenImages.has(image)) {
            return
          }
          seenImages.add(image)
          candidates.push({
            key: `${conceptId}-${index}`,
            image,
          })
        })
      })

      return candidates
    },
    chooseAddonPremiumPreview() {
      const candidates = this.premiumPreviewCandidates()
      if (!candidates.length) {
        this.addonPremiumPreview = null
        return
      }
      this.addonPremiumPreview = candidates[Math.floor(Math.random() * candidates.length)]
    },
    changeOptionQuantity(product, change) {
      const next = Math.min(this.maxOptionQuantity(product), Math.max(1, this.optionQuantity(product.id) + change))
      this.$emit("toggle-option", {
        product,
        selected: true,
        quantity: next,
      })
    },
    localizedValue(value) {
      if (value === null || value === undefined) {
        return ""
      }
      if (typeof value === "string") {
        try {
          return this.localizedValue(JSON.parse(value))
        } catch {
          return value
        }
      }
      if (typeof value === "object") {
        return value[this.lang] || value.kr || value.en || value.jp || ""
      }
      return String(value)
    },
    conceptTitle(concept) {
      if (!concept) {
        return ""
      }
      if (this.lang === "en") {
        return concept.title_en || concept.title || ""
      }
      if (this.lang === "jp") {
        return concept.title_jp || concept.title || ""
      }
      return concept.title || ""
    },
    conceptImage(concept) {
      if (!concept || !concept.thumbnail_image) {
        return ""
      }
      return `/upload/concept/thumbnail_image/${concept.thumbnail_image}`
    },
    patternImage(image) {
      return image ? `/upload/concept/thumbnail_image/${image}` : ""
    },
    patternLabel(index) {
      let value = index + 1
      let label = ""
      while (value > 0) {
        value -= 1
        label = String.fromCharCode(65 + (value % 26)) + label
        value = Math.floor(value / 26)
      }
      return label
    },
    staffName(staff) {
      return this.localizedValue(staff && staff.name)
    },
    staffInitial(staff) {
      return this.staffName(staff).trim().charAt(0).toUpperCase()
    },
    staffImage(staff) {
      if (!staff || !staff.profile_image || staff.profile_image.includes("user/profile_image/default.png")) {
        return ""
      }
      return `/upload/user/profile_image/${staff.profile_image}`
    },
    productTitle(product) {
      return this.localizedValue(product && product.title)
    },
    productSubtitle(product) {
      return this.localizedValue(product && (product.subtitle || product.subTitle))
    },
    optionTitle(product) {
      return this.localizedValue(product && product.title)
    },
    formatDate(date) {
      if (!date) {
        return ""
      }
      if (typeof date.format === "function") {
        if (this.lang === "en") {
          return date.format("MMM D, YYYY")
        }
        if (this.lang === "jp") {
          return date.format("YYYY年 M月 D日")
        }
        return date.format("YYYY년 M월 D일")
      }
      return String(date)
    },
    formatMoney(value) {
      return new Intl.NumberFormat(this.lang === "en" ? "en-US" : this.lang === "jp" ? "ja-JP" : "ko-KR").format(Number(value) || 0)
    },
    scrollSummaryIntoView(key) {
      window.clearTimeout(this.summaryScrollTimer)
      this.summaryScrollTimer = window.setTimeout(() => {
        if (this.activeSheet) {
          return
        }
        const row = this.$el.querySelector(`[data-summary-key="${key}"]`)
        if (!row || row.disabled) {
          return
        }
        const rowRect = row.getBoundingClientRect()
        const checkout = this.$refs.checkout
        const checkoutStyle = checkout ? window.getComputedStyle(checkout) : null
        const checkoutRect = checkout ? checkout.getBoundingClientRect() : null
        const boundary = checkoutStyle && checkoutStyle.position === "fixed" && checkoutRect ? checkoutRect.top - 12 : window.innerHeight - 12
        if (rowRect.bottom <= boundary && rowRect.top >= 0) {
          return
        }
        const offset = rowRect.bottom > boundary ? rowRect.bottom - boundary : rowRect.top - 12
        const reduceMotion = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches
        window.scrollBy({ top: offset, behavior: reduceMotion ? "auto" : "smooth" })
      }, 280)
    },
    hasImageError(key) {
      return !!this.imageErrors[key]
    },
    markImageError(key) {
      this.$set(this.imageErrors, key, true)
    },
  },
}
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@400;500;600;700&display=swap");

.booking-v2 {
  --rose: #d64f79;
  --rose-dark: #b9345d;
  --rose-soft: #fff1f4;
  --gold: #c99342;
  --gold-light: #ebd6ad;
  --ink: #2d2326;
  --muted: #817276;
  --line: #eadfdc;
  --booking-fixed-stack-height: calc(54px + 48px + 20px + env(safe-area-inset-bottom, 0px));
  --booking-font-body: "NanumSquare", sans-serif;
  --booking-font-display: "Cafe24Danjunghae", serif;
  width: 100%;
  max-width: 580px;
  min-height: calc(100vh - 72px);
  min-height: calc(100dvh - 72px);
  margin: 0 auto;
  padding: 18px 18px var(--booking-fixed-stack-height);
  color: var(--ink);
  background:
    linear-gradient(90deg, rgba(201, 147, 66, 0.055) 1px, transparent 1px) 12px 0 / calc(100% - 24px) 100% no-repeat,
    #ffffff;
  font-family: var(--booking-font-body);
  letter-spacing: 0;
}

.booking-v2[lang="ja"] {
  --booking-font-body: "Noto Sans JP", sans-serif;
  --booking-font-display: "Noto Sans JP", sans-serif;
}

button {
  font: inherit;
  letter-spacing: 0;
}

.booking-v2__shell {
  width: 100%;
  max-width: 520px;
  margin: 0 auto;
}

.booking-v2__header {
  padding: 5px 2px 14px;
  text-align: center;
}

.booking-v2__ornament {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 170px;
  margin: 0 auto 5px;
}

.booking-v2__ornament span {
  width: 62px;
  height: 1px;
  background: linear-gradient(90deg, transparent, var(--gold));
}

.booking-v2__ornament span:last-child {
  background: linear-gradient(90deg, var(--gold), transparent);
}

.booking-v2__ornament i {
  width: 8px;
  height: 8px;
  border: 1px solid var(--gold);
  transform: rotate(45deg);
}

.booking-v2__eyebrow {
  margin: 0 0 1px;
  color: #a57945;
  font-family: var(--booking-font-body);
  font-size: 10px;
  font-weight: 700;
}

.booking-v2 h1 {
  margin: 0;
  color: var(--rose);
  font-family: var(--booking-font-display);
  font-size: 30px;
  font-weight: 400;
  line-height: 1.25;
}

.booking-v2__progress {
  display: flex;
  justify-content: center;
  gap: 0;
  max-width: 380px;
  margin: 10px auto 0;
}

.booking-v2__progress span {
  position: relative;
  display: grid;
  flex: 1;
  min-width: 0;
  place-items: center;
}

.booking-v2__progress span::before {
  position: absolute;
  top: 4px;
  right: 50%;
  width: 100%;
  height: 1px;
  background: #e8ddda;
  content: "";
}

.booking-v2__progress span::after {
  position: absolute;
  z-index: 0;
  top: 4px;
  right: 50%;
  width: 100%;
  height: 1px;
  background: var(--rose);
  content: "";
  transform: scaleX(0);
  transform-origin: right center;
  transition: transform 0.22s ease-out;
}

.booking-v2__progress span:first-child::before {
  display: none;
}

.booking-v2__progress span:first-child::after {
  display: none;
}

.booking-v2__progress i {
  position: relative;
  z-index: 1;
  width: 9px;
  height: 9px;
  border: 1px solid #cdbfbb;
  border-radius: 50%;
  background: #ffffff;
  transform: scale(1);
  transition:
    border-color 0.18s ease,
    background-color 0.18s ease,
    box-shadow 0.22s ease;
}

.booking-v2__progress b {
  overflow: hidden;
  max-width: 100%;
  margin-top: 4px;
  color: #9c8e91;
  font-size: 10px;
  font-weight: 400;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.booking-v2__progress .is-complete i,
.booking-v2__progress .is-current i {
  border-color: var(--rose);
  background: var(--rose);
  box-shadow: 0 0 0 3px #fde5eb;
}

.booking-v2__progress .is-complete::after {
  transform: scaleX(1);
}

.booking-v2__progress .is-current i {
  animation: booking-progress-current 0.24s ease-out;
}

@keyframes booking-progress-current {
  0% {
    transform: scale(0.9);
  }

  100% {
    transform: scale(1);
  }
}

.booking-v2__progress .is-current b {
  color: var(--rose-dark);
  font-weight: 700;
}

.booking-v2__loading {
  display: flex;
  min-height: 320px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: var(--muted);
  font-size: 13px;
}

.booking-v2__concept-wrap {
  position: relative;
}

.booking-v2__concept {
  position: relative;
  display: grid;
  overflow: hidden;
  width: 100%;
  height: 156px;
  grid-template-columns: minmax(0, 1fr) 125px;
  border: 1px solid #d9b36e;
  border-radius: 6px;
  background: #fffdfb;
  box-shadow: 0 8px 24px rgba(83, 48, 57, 0.1);
}

.booking-v2__concept.is-feedback,
.booking-v2__summary-row.is-feedback {
  animation: booking-selection-feedback 0.4s ease-out;
}

@keyframes booking-selection-feedback {
  0% {
    background-color: rgba(255, 255, 255, 0);
  }

  40% {
    background-color: rgba(247, 194, 210, 0.38);
    box-shadow: inset 3px 0 0 rgba(214, 79, 121, 0.7);
  }

  100% {
    background-color: rgba(255, 255, 255, 0);
    box-shadow: inset 3px 0 0 rgba(214, 79, 121, 0);
  }
}

.booking-v2__concept-image {
  overflow: hidden;
  width: 125px;
  height: 156px;
  padding: 0;
  border: 0;
  border-left: 1px solid rgba(201, 147, 66, 0.38);
  background: #f8eef0;
  color: inherit;
  cursor: pointer;
}

.booking-v2__concept-image:disabled {
  cursor: default;
}

.booking-v2__concept-image:focus-visible {
  outline: 3px solid rgba(214, 79, 121, 0.42);
  outline-offset: -3px;
}

.booking-v2__concept-image img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center top;
}

.booking-v2__concept-placeholder {
  display: grid;
  width: 100%;
  height: 100%;
  place-items: center;
  background:
    radial-gradient(circle at center, #fff9f7 0, #f8e8ec 72%),
    #f8e8ec;
}

.booking-v2__concept-copy {
  display: flex;
  min-width: 0;
  padding: 18px 18px 17px 23px;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  background: #fffdfb;
}

.booking-v2__concept-heading {
  display: flex;
  width: 100%;
  min-width: 0;
  flex-direction: column;
  align-items: flex-start;
  color: var(--ink);
  text-align: left;
}

.booking-v2__concept-copy small {
  margin-bottom: 4px;
  color: #b9873f;
  font-size: 10px;
  font-weight: 800;
}

.booking-v2__concept-title-row {
  display: flex;
  width: 100%;
  min-width: 0;
  align-items: center;
  gap: 8px;
}

.booking-v2__concept-title-row strong {
  display: -webkit-box;
  overflow: hidden;
  min-width: 0;
  font-family: var(--booking-font-display);
  font-size: 24px;
  font-weight: 400;
  line-height: 1.25;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.booking-v2__concept-copy em {
  margin-top: 4px;
  color: #8b6b55;
  font-size: 11px;
  font-style: normal;
}

.booking-v2__concept-lock {
  flex: 0 0 auto;
  color: #b9873f;
}

.booking-v2__detail-button {
  display: inline-flex;
  min-height: 34px;
  margin-top: 12px;
  padding: 0 11px 0 12px;
  border: 1px solid #d2a760;
  border-radius: 4px;
  background: #ffffff;
  align-items: center;
  gap: 3px;
  color: #5b4540;
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

.booking-v2__detail-button .v-icon {
  color: #b9873f;
}

.booking-v2__summary {
  margin-top: 8px;
  border-top: 1px solid var(--line);
}

.booking-v2__summary-row {
  display: grid;
  width: 100%;
  min-height: 58px;
  grid-template-columns: 38px minmax(0, 1fr) 28px;
  align-items: center;
  padding: 7px 6px 7px 3px;
  border: 0;
  border-bottom: 1px solid var(--line);
  background: transparent;
  color: var(--ink);
  cursor: pointer;
  text-align: left;
  scroll-margin-bottom: var(--booking-fixed-stack-height);
  transition:
    background-color 0.18s ease,
    opacity 0.18s ease;
}

.booking-v2__summary-row:disabled {
  cursor: default;
  opacity: 0.46;
}

.booking-v2__summary-icon {
  display: grid;
  color: var(--gold);
  place-items: center;
}

.booking-v2__summary-icon .v-icon {
  color: var(--gold);
}

.booking-v2__summary-copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 2px;
}

.booking-v2__summary-copy small {
  color: var(--muted);
  font-size: 10px;
  font-weight: 700;
}

.booking-v2__summary-copy strong {
  overflow: hidden;
  color: var(--ink);
  font-size: 14px;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.booking-v2__summary-arrow {
  color: #b8aaad;
}

.booking-v2__checkout {
  position: fixed;
  z-index: 30;
  right: 0;
  bottom: calc(54px + env(safe-area-inset-bottom, 0px));
  left: 0;
  width: 100%;
  max-width: 580px;
  margin: 0 auto;
  padding: 0 max(12px, env(safe-area-inset-right, 0px)) 4px max(12px, env(safe-area-inset-left, 0px));
  background: transparent;
  transition:
    opacity 0.2s ease,
    visibility 0.2s ease;
}

.booking-v2__checkout.is-hidden {
  visibility: hidden;
  opacity: 0;
}

.booking-v2__checkout-inner {
  display: grid;
  box-sizing: border-box;
  max-width: 520px;
  height: 48px;
  grid-template-columns: minmax(0, 1fr) 128px;
  align-items: center;
  gap: 8px;
  margin: 0 auto;
  padding: 3px 5px 3px 12px;
  border: 1px solid rgba(255, 255, 255, 0.92);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.68);
  box-shadow:
    0 5px 16px rgba(63, 41, 47, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.96);
  -webkit-backdrop-filter: blur(18px) saturate(1.08);
  backdrop-filter: blur(18px) saturate(1.08);
}

.booking-v2__total {
  display: flex;
  min-width: 0;
  align-items: baseline;
  gap: 8px;
  white-space: nowrap;
}

.booking-v2__total small {
  color: var(--muted);
  font-size: 10px;
  text-shadow:
    0 1px 0 rgba(255, 255, 255, 0.96),
    0 0 8px rgba(255, 255, 255, 0.72);
}

.booking-v2__total strong {
  color: var(--ink);
  font-family: var(--booking-font-display);
  font-size: 22px;
  font-weight: 400;
  line-height: 1;
  text-shadow:
    0 1px 0 rgba(255, 255, 255, 0.96),
    0 0 9px rgba(255, 255, 255, 0.7);
}

.booking-v2__total em {
  margin-left: 3px;
  color: var(--muted);
  font-family: var(--booking-font-body);
  font-size: 11px;
  font-style: normal;
}

.booking-v2__pay {
  position: relative;
  isolation: isolate;
  overflow: hidden;
  min-height: 40px;
  border: 1px solid rgba(255, 229, 177, 0.96);
  border-radius: 6px;
  background: linear-gradient(118deg, #a91f58 0%, #e45391 24%, #f2b5c9 43%, #d7a052 55%, #ed76a7 73%, #a91f58 100%);
  background-size: 230% 100%;
  color: #ffffff;
  cursor: pointer;
  font-family: var(--booking-font-display);
  font-size: 16px;
  transform-origin: 50% 50%;
  will-change: transform, box-shadow, background-position;
  text-shadow:
    0 0 5px rgba(255, 255, 255, 0.98),
    0 0 12px rgba(255, 225, 238, 0.88),
    0 1px 0 rgba(119, 25, 59, 0.24);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.78),
    inset 0 -1px 0 rgba(135, 34, 68, 0.2),
    0 0 0 1px rgba(235, 163, 190, 0.2),
    0 4px 12px rgba(190, 48, 98, 0.24),
    0 0 18px rgba(222, 129, 169, 0.22);
  animation:
    booking-v2-pay-gradient 5.4s ease-in-out infinite,
    booking-v2-pay-heartbeat 2.8s ease-in-out infinite;
}

.booking-v2__pay::before {
  position: absolute;
  z-index: -1;
  top: -70%;
  bottom: -70%;
  left: -58%;
  width: 42%;
  background: linear-gradient(100deg, rgba(255, 255, 255, 0) 0%, rgba(255, 252, 240, 0.24) 24%, rgba(255, 255, 255, 0.92) 50%, rgba(255, 231, 191, 0.3) 72%, rgba(255, 255, 255, 0) 100%);
  content: "";
  filter: blur(1px);
  transform: skewX(-18deg);
  animation: booking-v2-pay-shimmer 3.8s ease-in-out infinite;
}

.booking-v2__pay > * {
  position: relative;
  z-index: 1;
}

.booking-v2__pay:active:not(:disabled) {
  animation: none;
  transform: scale(0.98);
}

.booking-v2__pay:disabled {
  border-color: rgba(255, 255, 255, 0.84);
  background: rgba(197, 185, 189, 0.78);
  cursor: default;
  animation: none;
  transform: none;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.72),
    0 2px 8px rgba(72, 53, 58, 0.08);
}

.booking-v2__pay:disabled::before {
  display: none;
}

@keyframes booking-v2-pay-gradient {
  0%,
  100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

@keyframes booking-v2-pay-heartbeat {
  0%,
  12%,
  25%,
  100% {
    transform: scale(1);
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.78),
      inset 0 -1px 0 rgba(135, 34, 68, 0.2),
      0 0 0 1px rgba(235, 163, 190, 0.2),
      0 4px 12px rgba(190, 48, 98, 0.24),
      0 0 18px rgba(222, 129, 169, 0.22);
  }
  6% {
    transform: scale(1.035);
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.84),
      inset 0 -1px 0 rgba(135, 34, 68, 0.16),
      0 0 0 2px rgba(255, 220, 232, 0.32),
      0 6px 16px rgba(190, 48, 98, 0.3),
      0 0 25px rgba(236, 142, 183, 0.42);
  }
  18% {
    transform: scale(1.022);
  }
}

@keyframes booking-v2-pay-shimmer {
  0%,
  18% {
    left: -58%;
    opacity: 0;
  }
  28% {
    opacity: 0.9;
  }
  58%,
  100% {
    left: 122%;
    opacity: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .booking-v2__pay {
    animation: none;
    background-position: 50% 50%;
  }

  .booking-v2__pay::before {
    animation: none;
  }
}

.booking-v2__overlay {
  position: fixed;
  z-index: 220;
  inset: 0;
  display: flex;
  width: 100%;
  height: 100vh;
  height: 100dvh;
  align-items: flex-end;
  justify-content: center;
  padding:
    max(8px, env(safe-area-inset-top, 0px))
    max(8px, env(safe-area-inset-right, 0px))
    max(8px, env(safe-area-inset-bottom, 0px))
    max(8px, env(safe-area-inset-left, 0px));
  background: rgba(35, 23, 27, 0.54);
  overscroll-behavior: none;
  -webkit-tap-highlight-color: transparent;
}

.booking-v2__sheet {
  display: flex;
  overflow: hidden;
  width: 100%;
  max-width: 560px;
  max-height: calc(82vh - 16px);
  max-height: calc(82dvh - 16px);
  flex-direction: column;
  border: 1px solid #d7b87c;
  border-radius: 22px 22px 4px 4px;
  outline: none;
  background: #ffffff;
  box-shadow: 0 20px 60px rgba(24, 14, 17, 0.34);
}

.booking-v2__sheet-handle {
  width: 42px;
  height: 4px;
  flex: 0 0 auto;
  margin: 8px auto 0;
  border-radius: 2px;
  background: #d8ccca;
}

.booking-v2__sheet-header {
  display: flex;
  min-height: 62px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px 8px 18px;
  border-bottom: 1px solid var(--line);
}

.booking-v2__sheet-header.is-pattern,
.booking-v2__sheet-header.is-staff {
  align-items: flex-start;
  padding-top: 14px;
  padding-bottom: 14px;
}

.booking-v2__sheet-header.is-pattern > div,
.booking-v2__sheet-header.is-staff > div {
  min-width: 0;
  padding-right: 10px;
}

.booking-v2__sheet-header small {
  color: var(--gold);
  font-size: 9px;
  font-weight: 800;
}

.booking-v2__sheet-header h2 {
  margin: 0;
  color: var(--rose);
  font-family: var(--booking-font-display);
  font-size: 24px;
  font-weight: 400;
  line-height: 1.2;
}

.booking-v2__sheet-header p {
  max-width: 420px;
  margin: 6px 0 0;
  color: var(--muted);
  font-size: 12px;
  line-height: 1.55;
}

.booking-v2__sheet-header p span {
  display: block;
}

.booking-v2__sheet-heading {
  min-width: 0;
  flex: 1 1 auto;
}

.booking-v2__sheet-flow-title {
  display: flex;
  min-height: 44px;
  align-items: center;
  gap: 11px;
}

.booking-v2__sheet-flow-title h2 {
  margin: 0;
}

.booking-v2__sheet-flow-title small {
  margin: 3px 0 0;
  color: #766d6f;
  font-family: var(--booking-font-body);
  font-size: 12px;
  font-weight: 700;
}

.booking-v2__sheet-header.is-product-flow .booking-v2__sheet-close {
  border: 1px solid #b58a4a;
  background: #ffffff;
  color: #8b704d;
}

.booking-v2__sheet-header.has-back {
  padding-left: 8px;
}

.booking-v2__sheet-back {
  display: grid;
  width: 44px;
  height: 44px;
  flex: 0 0 44px;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: var(--ink);
  cursor: pointer;
  place-items: center;
}

.booking-v2__sheet-close {
  display: grid;
  width: 44px;
  height: 44px;
  border: 0;
  border-radius: 50%;
  background: #f8f2f1;
  color: var(--ink);
  cursor: pointer;
  place-items: center;
}

.booking-v2__sheet-body {
  overflow-x: hidden;
  overflow-y: auto;
  min-height: 0;
  padding: 10px 14px 18px;
  flex: 1 1 auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
}

.booking-v2__sheet-body--pattern {
  padding-top: 12px;
}

.booking-v2__pattern-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.booking-v2__pattern-item {
  position: relative;
  overflow: hidden;
  min-width: 0;
  margin: 0;
  aspect-ratio: 4 / 5;
  border: 1px solid #e3cfaa;
  border-radius: 6px;
  background: #f9f1f2;
}

.booking-v2__pattern-item img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center top;
}

.booking-v2__pattern-badge {
  position: absolute;
  z-index: 1;
  top: 8px;
  left: 8px;
  display: grid;
  width: 32px;
  height: 32px;
  border: 1px solid rgba(255, 255, 255, 0.9);
  border-radius: 4px;
  background: #d65a82;
  box-shadow: 0 3px 10px rgba(60, 30, 38, 0.18);
  color: #ffffff;
  font-size: 14px;
  font-weight: 800;
  place-items: center;
}

.booking-v2__pattern-placeholder {
  display: grid;
  width: 100%;
  height: 100%;
  place-items: center;
}

.booking-v2__choice-list,
.booking-v2__product-list,
.booking-v2__option-list {
  display: grid;
  gap: 7px;
}

.booking-v2__visual-choice,
.booking-v2__staff-choice,
.booking-v2__product-choice,
.booking-v2__option-choice {
  border: 1px solid var(--line);
  border-radius: 6px;
  background: #ffffff;
  transition:
    border-color 0.18s ease,
    background-color 0.18s ease,
    box-shadow 0.18s ease;
}

.booking-v2__visual-choice {
  display: grid;
  width: 100%;
  min-height: 80px;
  grid-template-columns: 58px minmax(0, 1fr) 28px;
  align-items: center;
  gap: 12px;
  padding: 7px 10px 7px 7px;
  border: 1px solid var(--line);
  border-radius: 6px;
  background: #ffffff;
  color: var(--ink);
  cursor: pointer;
  text-align: left;
}

.booking-v2__visual-choice.is-selected,
.booking-v2__staff-choice.is-selected,
.booking-v2__product-choice.is-selected,
.booking-v2__option-choice.is-selected {
  border-color: #d9829c;
  background: #fff8fa;
}

.booking-v2__visual-choice > .v-icon,
.booking-v2__staff-select > .v-icon,
.booking-v2__product-select > .v-icon,
.booking-v2__option-toggle > .v-icon {
  color: #c5b5b8;
  transform: scale(0.92);
  transition:
    color 0.18s ease,
    transform 0.18s ease;
}

.booking-v2__visual-choice.is-selected > .v-icon,
.booking-v2__staff-choice.is-selected .booking-v2__staff-select > .v-icon,
.booking-v2__product-choice.is-selected .booking-v2__product-select > .v-icon,
.booking-v2__option-choice.is-selected .booking-v2__option-toggle > .v-icon {
  color: var(--rose);
  transform: scale(1);
}

.booking-v2__list-entry {
  animation: booking-list-entry 0.2s ease-out both;
  animation-delay: calc(var(--entry-index, 0) * 40ms);
}

@keyframes booking-list-entry {
  from {
    opacity: 0;
    transform: translateY(8px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.booking-v2__visual-media,
.booking-v2__staff-media {
  display: grid;
  overflow: hidden;
  background: #f7ecee;
  place-items: center;
}

.booking-v2__visual-media {
  width: 58px;
  height: 66px;
  border-radius: 3px;
}

.booking-v2__visual-media img,
.booking-v2__staff-media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.booking-v2__visual-choice strong,
.booking-v2__staff-select strong {
  overflow: hidden;
  font-size: 15px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.booking-v2__staff-choice {
  display: block;
}

.booking-v2__staff-select {
  display: grid;
  width: 100%;
  min-width: 0;
  min-height: 76px;
  grid-template-columns: 56px minmax(0, 1fr) 28px;
  align-items: center;
  gap: 11px;
  padding: 8px 7px;
  border: 0;
  background: transparent;
  color: var(--ink);
  cursor: pointer;
  text-align: left;
}

.booking-v2__staff-media {
  width: 56px;
  height: 56px;
  border-radius: 50%;
}

.booking-v2__staff-media b {
  color: var(--rose-dark);
  font-family: var(--booking-font-display);
  font-size: 22px;
  font-weight: 400;
}

.booking-v2__date-picker {
  display: grid;
  width: 100%;
  min-height: 58px;
  grid-template-columns: 44px minmax(0, 1fr) 44px;
  align-items: center;
  gap: 8px;
  padding: 3px 0;
}

.booking-v2__date-step,
.booking-v2__date-current {
  position: relative;
  display: grid;
  min-width: 44px;
  min-height: 44px;
  align-items: center;
  border: 1px solid #dfcfc9;
  border-radius: 8px;
  background: #ffffff;
  color: var(--ink);
  cursor: pointer;
  place-items: center;
}

.booking-v2__date-step .v-icon {
  color: var(--rose-dark);
}

.booking-v2__date-current {
  overflow: hidden;
  padding: 0 12px;
  color: var(--rose-dark);
  font-size: 14px;
  font-weight: 700;
  text-align: center;
}

.booking-v2__date-current input {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  cursor: pointer;
  opacity: 0;
}

.booking-v2__schedule-divider {
  height: 1px;
  margin: 8px 0 12px;
  background: var(--line);
}

.booking-v2__sheet-loading {
  display: grid;
  min-height: 154px;
  place-items: center;
}

.booking-v2__empty {
  display: grid;
  min-height: 154px;
  margin: 0;
  color: var(--muted);
  font-size: 13px;
  text-align: center;
  place-items: center;
}

.booking-v2__time-area {
  height: 154px;
}

.booking-v2__time-area > .booking-v2__sheet-loading,
.booking-v2__time-area > .booking-v2__empty {
  height: 100%;
  min-height: 0;
}

.booking-v2__time-grid {
  display: grid;
  height: 100%;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  grid-auto-rows: 44px;
  align-content: start;
  gap: 7px;
}

.booking-v2__time-grid button {
  min-width: 0;
  min-height: 44px;
  border: 1px solid #ddd0ce;
  border-radius: 4px;
  background: #ffffff;
  color: #55494c;
  cursor: pointer;
  font-size: 13px;
  transition:
    border-color 0.18s ease,
    background-color 0.18s ease,
    color 0.18s ease,
    transform 0.18s ease;
}

.booking-v2__time-grid button.is-selected {
  border-color: var(--rose);
  background: var(--rose);
  color: #ffffff;
}

.booking-v2__time-grid button:disabled {
  border-color: #eee9e7;
  background: #f3f0ef;
  color: #bcb3b1;
  text-decoration: line-through;
  cursor: default;
}

.booking-v2__product-choice,
.booking-v2__option-choice {
  overflow: hidden;
}

.booking-v2__product-select,
.booking-v2__option-toggle {
  display: grid;
  width: 100%;
  min-height: 72px;
  grid-template-columns: minmax(0, 1fr) auto 28px;
  align-items: center;
  gap: 8px;
  padding: 10px 10px 10px 13px;
  border: 0;
  background: transparent;
  color: var(--ink);
  cursor: pointer;
  text-align: left;
}

.booking-v2__product-select > span:first-child,
.booking-v2__option-toggle > span:first-child {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 2px;
}

.booking-v2__product-select strong,
.booking-v2__option-toggle strong {
  overflow-wrap: anywhere;
  font-size: 15px;
}

.booking-v2__product-select small,
.booking-v2__option-toggle small {
  overflow: hidden;
  color: var(--muted);
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.booking-v2__product-price {
  color: var(--rose-dark);
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
}

.booking-v2__product-price em {
  display: block;
  color: var(--muted);
  font-size: 9px;
  font-style: normal;
  font-weight: 400;
  text-align: right;
}

.booking-v2__stepper {
  display: grid;
  min-height: 51px;
  grid-template-columns: minmax(0, 1fr) 44px 36px 44px;
  align-items: center;
  padding: 3px 8px 3px 13px;
  border-top: 1px solid var(--line);
}

.booking-v2__stepper > span {
  color: var(--muted);
  font-size: 12px;
}

.booking-v2__stepper button {
  display: grid;
  width: 44px;
  height: 44px;
  border: 0;
  border-radius: 50%;
  background: #f7efee;
  cursor: pointer;
  place-items: center;
}

.booking-v2__stepper button:disabled {
  opacity: 0.35;
  cursor: default;
}

.booking-v2__stepper strong {
  font-size: 15px;
  text-align: center;
}

.booking-v2__product-flow {
  display: grid;
  gap: 10px;
}

.booking-v2__mode-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-items: start;
  gap: 8px;
}

.booking-v2__mode-card {
  overflow: hidden;
  min-width: 0;
  border: 1px solid #d6c4a8;
  border-radius: 7px;
  background: #ffffff;
  transition:
    border-color 0.18s ease,
    background-color 0.18s ease,
    box-shadow 0.18s ease;
}

.booking-v2__mode-card.is-selected {
  border-color: var(--rose);
  background: #fff8fa;
  box-shadow: 0 0 0 1px var(--rose);
}

.booking-v2__mode-select {
  display: block;
  width: 100%;
  padding: 0;
  border: 0;
  background: #ffffff;
  color: var(--ink);
  cursor: pointer;
}

.booking-v2__mode-image {
  display: block;
  overflow: hidden;
  width: 100%;
  aspect-ratio: 1 / 1;
  background: #f4edeb;
}

.booking-v2__mode-image img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center top;
}

.booking-v2__mode-copy {
  display: grid;
  min-height: 48px;
  padding: 8px 6px;
  align-content: center;
  text-align: center;
}

.booking-v2__mode-copy strong {
  font-family: var(--booking-font-display);
  font-size: 17px;
  font-weight: 400;
  line-height: 1.25;
}

.booking-v2__mode-stepper {
  display: grid;
  min-height: 48px;
  grid-template-columns: 44px minmax(0, 1fr) 44px;
  align-items: center;
  border-top: 1px solid #eadfda;
  background: #fff9fa;
  max-height: 48px;
  overflow: hidden;
}

.booking-v2__mode-stepper button {
  display: grid;
  width: 36px;
  height: 36px;
  margin: auto;
  border: 1px solid #e1d4d1;
  border-radius: 50%;
  background: #ffffff;
  color: #9c7741;
  cursor: pointer;
  place-items: center;
}

.booking-v2__mode-stepper button:disabled {
  color: #c8bec0;
  cursor: default;
  opacity: 0.55;
}

.booking-v2__mode-stepper strong {
  overflow-wrap: anywhere;
  font-size: 13px;
  font-weight: 700;
  text-align: center;
}

.booking-v2__benefit-list,
.booking-v2__pattern-choice-list,
.booking-v2__addon-list {
  display: grid;
  gap: 0;
  border-top: 1px solid var(--line);
}

.booking-v2__addon-intro {
  display: grid;
  gap: 2px;
  padding: 2px 12px 10px;
  text-align: center;
}

.booking-v2__addon-intro strong,
.booking-v2__addon-intro span {
  letter-spacing: 0;
}

.booking-v2__addon-intro strong {
  color: var(--rose-dark);
  font-size: 11px;
  font-weight: 600;
  line-height: 1.4;
}

.booking-v2__addon-intro span {
  color: #807478;
  font-size: 10px;
  line-height: 1.4;
}

.booking-v2__benefit-choice {
  display: grid;
  width: 100%;
  min-height: 92px;
  grid-template-columns: 68px minmax(0, 1fr) 28px;
  align-items: center;
  gap: 11px;
  padding: 10px 12px 10px 9px;
  border: 0;
  border-bottom: 1px solid var(--line);
  border-radius: 0;
  background: #ffffff;
  color: var(--ink);
  cursor: pointer;
  text-align: left;
  transition:
    background-color 0.18s ease,
    box-shadow 0.18s ease;
}

.booking-v2__benefit-choice.is-selected,
.booking-v2__pattern-choice.is-selected {
  background: #fff8fa;
  box-shadow: inset 3px 0 0 var(--rose);
}

.booking-v2__benefit-choice > .v-icon,
.booking-v2__pattern-choice > .v-icon {
  color: #c7b9bc;
  transform: scale(0.92);
  transition:
    color 0.18s ease,
    transform 0.18s ease;
}

.booking-v2__benefit-choice.is-selected > .v-icon,
.booking-v2__pattern-choice.is-selected > .v-icon {
  color: var(--rose);
  transform: scale(1);
}

.booking-v2__benefit-media {
  display: grid;
  width: 68px;
  height: 68px;
  border-right: 1px solid #eadfda;
  color: #bb8b45;
  place-items: center;
}

.booking-v2__benefit-copy,
.booking-v2__pattern-choice-copy,
.booking-v2__addon-copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.booking-v2__benefit-copy {
  gap: 3px;
}

.booking-v2__benefit-copy small,
.booking-v2__pattern-choice-copy small {
  width: fit-content;
  padding: 2px 5px;
  border-radius: 2px;
  background: var(--rose);
  color: #ffffff;
  font-size: 9px;
  font-weight: 800;
}

.booking-v2__recommendation-badge {
  transform-origin: center;
  animation: booking-recommendation-pulse 1.35s ease-in-out infinite;
  box-shadow: 0 0 0 0 rgba(214, 79, 121, 0.32);
}

.booking-v2__benefit-copy strong,
.booking-v2__pattern-choice-copy strong {
  font-size: 15px;
  line-height: 1.35;
}

.booking-v2__benefit-copy em,
.booking-v2__pattern-choice-copy em,
.booking-v2__addon-copy em,
.booking-v2__flow-summary em {
  color: var(--rose-dark);
  font-size: 13px;
  font-style: normal;
  font-weight: 700;
}

.booking-v2__print {
  box-sizing: border-box;
  display: grid;
  overflow: hidden;
  width: 40px;
  height: 50px;
  padding: 1px;
  border: 1px solid #d9c6b0;
  background: #ffffff;
  box-shadow: 0 3px 8px rgba(73, 44, 38, 0.14);
  place-items: center;
}

.booking-v2__print img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center top;
}

.booking-v2__print--single {
  position: absolute;
  z-index: 2;
  top: 4px;
  left: 0;
  transform: rotate(-3deg);
}

.booking-v2__gold-print-stack {
  position: relative;
  display: block;
  width: 60px;
  height: 58px;
}

.booking-v2__print--split {
  position: absolute;
  z-index: 1;
  top: 5px;
  left: 20px;
  grid-template-rows: repeat(2, minmax(0, 1fr));
  gap: 1px;
  transform: rotate(4deg);
}

.booking-v2__print-split-panel {
  position: relative;
  display: grid;
  overflow: hidden;
  min-height: 0;
  place-items: center;
}

.booking-v2__print-split-panel img {
  width: 62.5%;
  height: 160%;
  transform: rotate(90deg);
  transform-origin: center;
}

@keyframes booking-recommendation-pulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(214, 79, 121, 0.32);
  }

  50% {
    opacity: 0.7;
    transform: scale(1.06);
    box-shadow: 0 0 0 4px rgba(214, 79, 121, 0.08);
  }
}

.booking-v2__pattern-choice {
  display: grid;
  width: 100%;
  min-height: 82px;
  grid-template-columns: 28px minmax(0, 1fr) 92px;
  align-items: center;
  gap: 9px;
  padding: 8px 11px;
  border: 0;
  border-bottom: 1px solid var(--line);
  border-radius: 0;
  background: #ffffff;
  color: var(--ink);
  cursor: pointer;
  text-align: left;
  transition:
    background-color 0.18s ease,
    box-shadow 0.18s ease;
}

.booking-v2__pattern-choice-copy {
  gap: 2px;
}

.booking-v2__pattern-choice-heading {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 5px;
}

.booking-v2__pattern-choice-heading strong,
.booking-v2__pattern-choice-heading small {
  flex: 0 0 auto;
}

.booking-v2__print-stack {
  position: relative;
  display: block;
  width: 86px;
  height: 58px;
}

.booking-v2__print-stack .booking-v2__print {
  position: absolute;
  top: 5px;
  right: calc(var(--print-index) * 18px);
  transform: rotate(calc((var(--print-index) - 1) * -3deg));
}

.booking-v2__flow-note {
  margin: 0 2px;
  color: var(--muted);
  font-size: 11px;
  line-height: 1.5;
}

.booking-v2__benefit-choice--group {
  min-height: 78px;
}

.booking-v2__group-stepper {
  border: 1px solid var(--line);
  border-radius: 6px;
}

.booking-v2__addon-choice {
  overflow: hidden;
  border: 0;
  border-bottom: 1px solid var(--line);
  border-radius: 0;
  background: #ffffff;
  transition:
    border-color 0.18s ease,
    background-color 0.18s ease;
}

.booking-v2__addon-choice.is-selected {
  border-color: #d77b98;
  background: #fffafb;
}

.booking-v2__addon-row {
  display: grid;
  min-height: 68px;
  grid-template-columns: 40px minmax(0, 1fr) auto;
  align-items: center;
  gap: 6px;
  padding: 7px 10px 7px 5px;
}

.booking-v2__addon-toggle {
  display: grid;
  width: 40px;
  height: 44px;
  border: 0;
  background: transparent;
  color: #c9bcbf;
  cursor: pointer;
  place-items: center;
}

.booking-v2__addon-choice.is-selected .booking-v2__addon-toggle {
  color: var(--rose);
}

.booking-v2__addon-toggle .v-icon {
  color: currentColor;
  transform: scale(0.92);
  transition:
    color 0.18s ease,
    transform 0.18s ease;
}

.booking-v2__addon-choice.is-selected .booking-v2__addon-toggle .v-icon {
  transform: scale(1);
}

.booking-v2__addon-toggle:focus-visible {
  border-radius: 50%;
  outline-offset: -6px;
}

.booking-v2__addon-copy {
  gap: 3px;
}

.booking-v2__addon-copy strong {
  overflow-wrap: anywhere;
  font-size: 14px;
  line-height: 1.35;
}

.booking-v2__addon-detail {
  display: flex;
  min-height: 36px;
  padding: 0 2px 0 8px;
  border: 0;
  background: transparent;
  align-items: center;
  gap: 1px;
  color: #7f6e72;
  cursor: pointer;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}

.booking-v2__addon-detail .v-icon {
  transform: rotate(0);
  transition: transform 0.18s ease;
}

.booking-v2__addon-detail .v-icon.is-open {
  transform: rotate(180deg);
}

.booking-v2__addon-description {
  box-sizing: border-box;
  overflow: hidden;
  padding: 11px 13px 12px 51px;
  border-top: 1px solid #eee5e3;
  background: #fff7f9;
}

.booking-v2__addon-description p {
  margin: 0;
  color: #66585b;
  font-size: 12px;
  line-height: 1.65;
  white-space: pre-line;
}

.booking-v2__addon-description.is-logo-removal {
  padding: 0;
  background: #ffffff;
}

.booking-v2__addon-description.is-concept-addon {
  padding: 0;
  background: #ffffff;
}

.booking-v2__addon-description.is-retouch-addon {
  padding: 0;
  background: #ffffff;
}

.booking-v2__concept-addon-detail {
  padding: 12px 13px 14px;
}

.booking-v2__concept-addon-title {
  display: block;
  color: var(--rose-dark);
  font-size: 14px;
  line-height: 1.4;
  text-align: center;
}

.booking-v2__concept-addon-comparison {
  position: relative;
  display: grid;
  overflow: hidden;
  height: clamp(150px, 42vw, 184px);
  margin-top: 9px;
  border: 1px solid #eaded9;
  border-radius: 5px;
  background: #f5efed;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.booking-v2__concept-addon-shot {
  position: relative;
  overflow: hidden;
  min-width: 0;
  margin: 0;
}

.booking-v2__concept-addon-shot + .booking-v2__concept-addon-shot {
  border-left: 1px solid rgba(255, 255, 255, 0.88);
}

.booking-v2__concept-addon-shot img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 26%;
}

.booking-v2__concept-addon-shot figcaption {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  display: grid;
  min-height: 30px;
  padding: 5px 4px;
  background: rgba(48, 35, 38, 0.66);
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  line-height: 1.25;
  text-align: center;
  place-items: center;
}

.booking-v2__concept-addon-plus {
  position: absolute;
  z-index: 1;
  top: 50%;
  left: 50%;
  display: grid;
  width: 36px;
  height: 36px;
  border: 1px solid #d9a253;
  border-radius: 50%;
  background: #ffffff;
  color: #c98c36;
  transform: translate(-50%, -50%);
  place-items: center;
}

.booking-v2__concept-addon-plus .v-icon {
  color: currentColor;
}

.booking-v2__addon-description .booking-v2__concept-addon-note {
  margin-top: 9px;
  padding: 9px 8px;
  border-radius: 4px;
  background: #fff3f6;
  color: var(--rose-dark);
  font-size: 11px;
  font-weight: 600;
  line-height: 1.45;
  text-align: center;
}

.booking-v2__retouch-addon-detail {
  padding: 12px 13px 14px;
}

.booking-v2__retouch-addon-title {
  display: block;
  color: var(--rose-dark);
  font-size: 14px;
  line-height: 1.4;
  text-align: center;
}

.booking-v2__retouch-addon-comparison {
  display: grid;
  width: min(100%, 270px);
  margin: 9px auto 0;
  grid-template-columns: minmax(0, 1fr) 28px minmax(0, 1fr);
  align-items: center;
}

.booking-v2__retouch-addon-shot {
  overflow: hidden;
  min-width: 0;
  margin: 0;
  aspect-ratio: 582 / 761;
  border-radius: 5px;
  background: #f5efed;
}

.booking-v2__retouch-addon-shot img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.booking-v2__retouch-addon-plus {
  color: var(--rose) !important;
  justify-self: center;
}

.booking-v2__addon-description .booking-v2__retouch-addon-note {
  margin-top: 9px;
  color: #66585b;
  font-size: 11px;
  line-height: 1.5;
  text-align: center;
}

.booking-v2__logo-removal-detail {
  padding: 12px 13px 15px;
}

.booking-v2__logo-removal-title,
.booking-v2__logo-removal-use-title {
  display: block;
  color: var(--rose-dark);
  font-size: 14px;
  line-height: 1.4;
  text-align: center;
}

.booking-v2__logo-removal-title {
  margin-bottom: 0;
}

.booking-v2__logo-removal-preview {
  display: grid;
  width: min(100%, 260px);
  margin: 0 auto;
  grid-template-columns: minmax(0, 1fr) 24px minmax(0, 1fr);
  align-items: center;
}

.booking-v2__logo-removal-shot {
  min-width: 0;
  margin: 0;
}

.booking-v2__logo-removal-photo {
  overflow: hidden;
  aspect-ratio: 321 / 549;
  border-radius: 5px;
  background: #fff;
}

.booking-v2__logo-removal-photo > img:first-child {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.booking-v2__logo-removal-shot figcaption {
  margin-top: 5px;
  color: #463a3d;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.35;
  text-align: center;
}

.booking-v2__logo-removal-arrow {
  color: #9b8d90 !important;
  justify-self: center;
}

.booking-v2__addon-description .booking-v2__logo-removal-note {
  margin: 2px 0 9px;
  color: #66585b;
  font-size: 11px;
  line-height: 1.5;
  text-align: center;
}

.booking-v2__logo-removal-use-title {
  margin-top: 11px;
}

.booking-v2__addon-description .booking-v2__logo-removal-use-copy {
  margin-top: 3px;
  color: #66585b;
  font-size: 11px;
  line-height: 1.55;
  text-align: center;
}

.booking-v2__logo-removal-uses {
  margin-top: 10px;
}

.booking-v2__logo-removal-use-cases {
  display: block;
  width: 100%;
  height: auto;
}

.booking-v2__logo-removal-use-labels {
  display: grid;
  margin-top: 4px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 7px;
  color: #54484b;
  font-size: 10px;
  font-weight: 700;
  line-height: 1.35;
  text-align: center;
}

.booking-v2__flow-summary {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 9px;
  margin: 4px 0 0;
  padding: 10px 12px;
  border-top: 1px solid #e4d4ce;
  color: var(--muted);
  font-size: 11px;
}

.booking-v2__flow-summary strong {
  overflow: hidden;
  color: var(--ink);
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.booking-v2__sheet-footer {
  flex: 0 0 auto;
  padding: 8px 14px max(10px, env(safe-area-inset-bottom, 0px));
  border-top: 1px solid var(--line);
  background: #ffffff;
}

.booking-v2__sheet-footer button {
  width: 100%;
  min-height: 48px;
  border: 1px solid var(--rose-dark);
  border-radius: 4px;
  background: var(--rose);
  color: #ffffff;
  cursor: pointer;
  font-family: var(--booking-font-display);
  font-size: 17px;
  transition:
    border-color 0.18s ease,
    background-color 0.18s ease,
    color 0.18s ease,
    box-shadow 0.18s ease;
}

.booking-v2__sheet-footer--flow {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 132px;
  align-items: center;
  gap: 10px;
  padding-top: 7px;
  padding-right: 12px;
  padding-left: 12px;
  box-shadow: 0 -7px 18px rgba(66, 42, 48, 0.06);
}

.booking-v2__flow-total {
  display: block;
  min-width: 0;
  padding-left: 3px;
}

.booking-v2__flow-total > small {
  display: block;
  margin-bottom: 3px;
  color: var(--muted);
  font-size: 10px;
}

.booking-v2__flow-total > strong {
  display: block;
  color: var(--ink);
  font-family: var(--booking-font-display);
  font-size: 24px;
  font-weight: 400;
  line-height: 1;
  white-space: nowrap;
}

.booking-v2__flow-total > strong em {
  margin-left: 3px;
  color: var(--muted);
  font-family: var(--booking-font-body);
  font-size: 10px;
  font-style: normal;
}

.booking-v2__flow-total > span {
  display: block;
  color: var(--muted);
  font-size: 11px;
  line-height: 1.35;
}

.booking-v2__flow-actions {
  display: grid;
  gap: 0;
  overflow: hidden;
}

.booking-v2__flow-actions button:not(:disabled) {
  animation: booking-action-ready 0.4s ease-out;
}

@keyframes booking-action-ready {
  0% {
    box-shadow: 0 0 0 rgba(214, 79, 121, 0);
  }

  50% {
    box-shadow: 0 0 0 3px rgba(214, 79, 121, 0.18);
  }

  100% {
    box-shadow: 0 0 0 rgba(214, 79, 121, 0);
  }
}

.booking-v2__sheet-footer--flow .booking-v2__flow-actions > button {
  min-height: 46px;
}

.booking-v2__sheet-footer button:disabled {
  border-color: #d8ced0;
  background: #ddd5d7;
  cursor: default;
}

.booking-v2__group-price-breakdown {
  display: block;
  color: var(--muted);
  font-size: 9px;
  line-height: 1.3;
}

.booking-product-forward-enter-active,
.booking-product-backward-enter-active {
  transition:
    opacity 0.16s ease-out,
    transform 0.16s ease-out;
  will-change: opacity, transform;
}

.booking-product-forward-leave-active,
.booking-product-backward-leave-active {
  transition:
    opacity 0.1s ease-in,
    transform 0.1s ease-in;
  will-change: opacity, transform;
}

.booking-product-forward-enter,
.booking-product-backward-leave-to {
  opacity: 0;
  transform: translateX(18px);
}

.booking-product-forward-leave-to,
.booking-product-backward-enter {
  opacity: 0;
  transform: translateX(-18px);
}

.booking-heading-forward-enter-active,
.booking-heading-backward-enter-active {
  transition:
    opacity 0.12s ease-out,
    transform 0.12s ease-out;
}

.booking-heading-forward-leave-active,
.booking-heading-backward-leave-active {
  transition:
    opacity 0.08s ease-in,
    transform 0.08s ease-in;
}

.booking-heading-forward-enter,
.booking-heading-backward-leave-to {
  opacity: 0;
  transform: translateX(8px);
}

.booking-heading-forward-leave-to,
.booking-heading-backward-enter {
  opacity: 0;
  transform: translateX(-8px);
}

.booking-content-swap-enter-active {
  transition:
    opacity 0.2s ease-out,
    transform 0.2s ease-out;
}

.booking-content-swap-leave-active {
  transition: opacity 0.12s ease-in;
}

.booking-content-swap-enter {
  opacity: 0;
  transform: translateY(8px);
}

.booking-content-swap-leave-to {
  opacity: 0;
}

.booking-expand-enter-active,
.booking-expand-leave-active {
  overflow: hidden;
  transition:
    max-height 0.22s ease,
    opacity 0.18s ease,
    transform 0.22s ease;
}

.booking-expand-enter,
.booking-expand-leave-to {
  max-height: 0;
  opacity: 0;
  transform: translateY(-6px);
}

.booking-addon-detail-enter-active {
  overflow: hidden;
  transition:
    height 0.32s ease-out,
    opacity 0.22s ease-out,
    transform 0.32s ease-out;
  will-change: height, opacity, transform;
}

.booking-addon-detail-leave-active {
  overflow: hidden;
  transition:
    height 0.22s ease-in,
    opacity 0.18s ease-in,
    transform 0.22s ease-in;
  will-change: height, opacity, transform;
}

.booking-price-enter-active {
  transition:
    opacity 0.11s ease-out,
    transform 0.11s ease-out,
    text-shadow 0.2s ease-out;
}

.booking-price-leave-active {
  transition:
    opacity 0.09s ease-in,
    transform 0.09s ease-in;
}

.booking-price-enter {
  opacity: 0;
  transform: translateY(6px);
  text-shadow: 0 0 10px rgba(214, 79, 121, 0.42);
}

.booking-price-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.booking-count-enter-active,
.booking-count-leave-active {
  transition:
    opacity 0.07s ease,
    transform 0.07s ease;
}

.booking-count-enter {
  opacity: 0;
  transform: translateY(4px) scale(0.96);
}

.booking-count-leave-to {
  opacity: 0;
  transform: translateY(-4px) scale(0.96);
}

.booking-action-label-enter-active,
.booking-action-label-leave-active {
  transition:
    opacity 0.09s ease,
    transform 0.09s ease;
}

.booking-action-label-enter {
  opacity: 0;
  transform: translateY(4px);
}

.booking-action-label-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

.booking-sheet-enter-active,
.booking-sheet-leave-active {
  transition: background-color 0.22s ease;
}

.booking-sheet-enter-active .booking-v2__sheet,
.booking-sheet-leave-active .booking-v2__sheet {
  transition:
    transform 0.24s ease,
    opacity 0.2s ease;
}

.booking-sheet-enter,
.booking-sheet-leave-to {
  background: rgba(35, 23, 27, 0);
}

.booking-sheet-enter .booking-v2__sheet,
.booking-sheet-leave-to .booking-v2__sheet {
  transform: translateY(28px);
  opacity: 0;
}

button:focus-visible {
  outline: 2px solid var(--rose);
  outline-offset: 2px;
}

@media (max-width: 420px) {
  .booking-v2 {
    padding-right: 12px;
    padding-left: 12px;
  }

  .booking-v2__concept {
    height: 148px;
    grid-template-columns: minmax(0, 1fr) 118px;
  }

  .booking-v2__concept-copy {
    padding: 15px 14px 14px 18px;
  }

  .booking-v2__concept-image {
    width: 118px;
    height: 148px;
  }

  .booking-v2__concept-title-row strong {
    font-size: 22px;
  }

  .booking-v2__detail-button {
    min-height: 32px;
    margin-top: 9px;
    padding-right: 9px;
    padding-left: 10px;
    font-size: 11px;
  }

  .booking-v2__sheet-header p {
    font-size: 11px;
  }

  .booking-v2__time-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .booking-v2__benefit-choice {
    min-height: 84px;
    grid-template-columns: 58px minmax(0, 1fr) 25px;
    gap: 8px;
    padding-right: 9px;
    padding-left: 7px;
  }

  .booking-v2__benefit-media {
    width: 58px;
    height: 62px;
  }

  .booking-v2__benefit-copy strong,
  .booking-v2__pattern-choice-copy strong {
    font-size: 14px;
  }

  .booking-v2__pattern-choice {
    min-height: 76px;
    grid-template-columns: 25px minmax(0, 1fr) 80px;
    gap: 6px;
    padding-right: 8px;
    padding-left: 8px;
  }

  .booking-v2__print-stack {
    width: 76px;
  }

  .booking-v2__print-stack .booking-v2__print {
    right: calc(var(--print-index) * 15px);
  }

  .booking-v2__addon-row {
    grid-template-columns: 38px minmax(0, 1fr) auto;
    padding-right: 7px;
  }

  .booking-v2__addon-detail span {
    font-size: 10px;
  }
}

@media (max-width: 359px) {
  .booking-v2 {
    padding-right: 9px;
    padding-left: 9px;
  }

  .booking-v2__sheet-body {
    padding-right: 9px;
    padding-left: 9px;
  }

  .booking-v2__mode-grid {
    gap: 6px;
  }

  .booking-v2__mode-copy {
    min-height: 62px;
    padding-right: 4px;
    padding-left: 4px;
  }

  .booking-v2__mode-stepper {
    grid-template-columns: 40px minmax(0, 1fr) 40px;
  }

  .booking-v2__mode-stepper button {
    width: 34px;
    height: 34px;
  }

  .booking-v2__checkout-inner {
    grid-template-columns: minmax(0, 1fr) 112px;
  }
}

@media (max-height: 500px) and (orientation: landscape) {
  .booking-v2 {
    padding-bottom: calc(62px + env(safe-area-inset-bottom, 0px));
  }

  .booking-v2__checkout {
    position: static;
    width: 100%;
    margin-top: 12px;
    padding: 0;
  }

  .booking-v2__checkout-inner {
    max-width: 520px;
  }

  .booking-v2__sheet {
    max-height: calc(100vh - 16px);
    max-height: calc(100dvh - max(16px, env(safe-area-inset-top, 0px)) - max(16px, env(safe-area-inset-bottom, 0px)));
  }

  .booking-v2__sheet-header {
    min-height: 54px;
  }

  .booking-v2__sheet-header h2 {
    font-size: 21px;
  }

  .booking-v2__sheet-header.is-pattern,
  .booking-v2__sheet-header.is-staff {
    padding-top: 9px;
    padding-bottom: 9px;
  }

  .booking-v2__sheet-header p {
    margin-top: 3px;
    font-size: 10px;
    line-height: 1.35;
  }

  .booking-v2__mode-image {
    aspect-ratio: 16 / 7;
  }

  .booking-v2__mode-copy {
    min-height: 40px;
    padding: 5px 6px;
  }
}

@media (min-width: 768px) {
  .booking-v2 {
    padding-top: 28px;
  }

  .booking-v2__concept {
    height: 176px;
    grid-template-columns: minmax(0, 1fr) 141px;
  }

  .booking-v2__concept-copy {
    padding: 22px 22px 21px 26px;
  }

  .booking-v2__concept-image {
    width: 141px;
    height: 176px;
  }

  .booking-v2__pattern-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .booking-v2__sheet {
    max-height: min(760px, calc(100vh - 32px));
    max-height: min(760px, calc(100dvh - 32px));
  }
}

@media (prefers-reduced-motion: reduce) {
  .booking-v2__recommendation-badge,
  .booking-v2__pay:not(:disabled),
  .booking-v2__progress .is-current i,
  .booking-v2__concept.is-feedback,
  .booking-v2__summary-row.is-feedback,
  .booking-v2__list-entry,
  .booking-v2__flow-actions button:not(:disabled) {
    animation: none;
  }

  .booking-v2__progress span::after,
  .booking-v2__progress i,
  .booking-v2__summary-row,
  .booking-v2__visual-choice,
  .booking-v2__staff-choice,
  .booking-v2__visual-choice > .v-icon,
  .booking-v2__staff-select > .v-icon,
  .booking-v2__time-grid button,
  .booking-v2__mode-card,
  .booking-v2__benefit-choice,
  .booking-v2__pattern-choice,
  .booking-v2__benefit-choice > .v-icon,
  .booking-v2__pattern-choice > .v-icon,
  .booking-v2__addon-choice,
  .booking-v2__addon-toggle .v-icon,
  .booking-v2__addon-detail .v-icon,
  .booking-v2__sheet-footer button,
  .booking-product-forward-enter-active,
  .booking-product-forward-leave-active,
  .booking-product-backward-enter-active,
  .booking-product-backward-leave-active,
  .booking-heading-forward-enter-active,
  .booking-heading-forward-leave-active,
  .booking-heading-backward-enter-active,
  .booking-heading-backward-leave-active,
  .booking-content-swap-enter-active,
  .booking-content-swap-leave-active,
  .booking-expand-enter-active,
  .booking-expand-leave-active,
  .booking-addon-detail-enter-active,
  .booking-addon-detail-leave-active,
  .booking-price-enter-active,
  .booking-price-leave-active,
  .booking-count-enter-active,
  .booking-count-leave-active,
  .booking-action-label-enter-active,
  .booking-action-label-leave-active,
  .booking-sheet-enter-active,
  .booking-sheet-leave-active,
  .booking-sheet-enter-active .booking-v2__sheet,
  .booking-sheet-leave-active .booking-v2__sheet {
    transition-duration: 0.01ms;
  }
}
</style>
